import { useCallback } from 'react';
import { useApi } from '@/hooks/useApi';
import { AUTH_ERROR, SessionExpiredError } from '@/context/AuthSessionContext';
import { downloadViaApi } from '@/utils/download';
import type {
  BulkCrmLeadsResult,
  BulkRejectCrmLeadsDto,
  ChangeCrmDealStageDto,
  ConvertCrmLeadDto,
  ConvertCrmLeadResult,
  CreateCrmActivityDto,
  CreateCrmLeadDto,
  CrmAccount,
  CrmAccountDetail,
  CrmAccountListItem,
  CrmAccountListParams,
  CrmActivity,
  CrmContact,
  CrmContractorCandidate,
  CrmCreateContractorResult,
  CrmDealDetail,
  CrmDealListItem,
  CrmLeadDetail,
  CrmLeadListItem,
  CrmUserOption,
  LinkCrmContractorDto,
  PatchCrmDealDto,
  CrmLeadIdsDto,
  CrmLeadListParams,
  CrmStage,
  RejectCrmLeadDto,
  UpsertCrmAccountDto,
  UpsertCrmContactDto,
  UpsertCrmDealDto,
  ApiResponse,
  LogoInk,
  Tag,
  TagModule,
  UserRoleName,
  PermissionModule,
  CreateTagDto,
  UpdateTagDto,
  CompanyListItem,
  CompanyDetail,
  UpdateCompanyDto,
  CreateCompanyDto,
  DeleteCompanyMode,
  DeleteCompanyResult,
  InvoiceListItem,
  InvoiceDetail,
  InvoiceFilterParams,
  PaginatedResponse,
  InvoiceStatus,
  UpdateInvoiceDto,
  BulkDeleteInvoicesResponse,
  RegistryCompanyData,
  ContractorRegistryRefreshResult,
  VatPayerData,
  ContractorListItem,
  ContractorDetail,
  ContractorDocument,
  UpdateContractorDocumentDto,
  UpdateContractorDto,
  DashboardResponse,
  FinancialOverview,
  ContractorDashboardResponse,
  SalesSummary,
  KsefConfig,
  UpdateKsefConfigDto,
  KsefSyncResult,
  KsefTestConnectionResult,
  MBankPaymentRecord,
  MBankExportRequest,
  ReimbursementExportRecord,
  VehicleListItem,
  CreateVehicleDto,
  UpdateVehicleDto,
  CreateDriverDto,
  StandardRouteListItem,
  CreateStandardRouteDto,
  UpdateStandardRouteDto,
  CalculateDistanceDto,
  CalculateDistanceResult,
  MileageRecordSummary,
  MileageRecordDetail,
  MileageEntryItem,
  CreateMileageEntryDto,
  UpdateMileageEntryDto,
  TripCandidate,
  ReconciliationResult,
  MileagePlace,
  CreateMileagePlaceDto,
  UpdateMileagePlaceDto,
  GenerateTripsResult,
  AssignInvoicesResponse,
  FxRatesResponse,
  InvoicePaymentItem,
  AddInvoicePaymentDto,
  AddInvoicePaymentResponse,
  ReceivableCustomer,
  SalesStatementCustomer,
  SalesStatementRequest,
  CombinedPaymentSearchRequest,
  CombinedPaymentSearchResult,
  CombinedPaymentDetail,
  CreateCombinedPaymentDto,
  SalesCombinedPaymentCustomer,
  SalesCombinedPaymentSearchRequest,
  CreateSalesCombinedPaymentDto,
  InvoiceNote,
  Project,
  ProjectListItem,
  ProjectOption,
  ProjectAnalytics,
  CreateProjectDto,
  UpdateProjectDto,
  LineItemProjectAssignment,
  ProjectTagRulesDto,
  ProjectTagRuleApplyResult,
  BulkAssignInvoiceProjectResult,
  SignatureVerification,
  KsefEnvironment,
  FiscalSettingsDto,
  FiscalCertificateInfo,
  FiscalPrinterDto,
  FiscalApiTokenDto,
  FiscalInvoiceStatus,
  FiscalReceiptStatus,
  FiscalInvoiceInput,
  FiscalReceiptInput,
  FiscalStatsDto,
  FiscalNumberingCounterDto,
  WarehouseItem,
  CreateWarehouseItemDto,
  UpdateWarehouseItemDto,
  WzListItem,
  WzDetail,
  CreateWzDto,
  UpdateWzDto,
  WzListParams,
  UploadSignedWzResult,
  ApiApplicationDto,
} from '@ip/shared-types';

// Re-export for consumers that imported from api.ts
export type { MBankPaymentRecord, MBankExportRequest };
export { SessionExpiredError };

/** Thrown by createCompany when the server reports a duplicate NIP (409). */
export class DuplicateNipError extends Error {}

/**
 * Thrown by the /users/directory endpoints when the backend answers
 * 503 DIRECTORY_UNAVAILABLE (Graph User.Read.All permission not granted yet /
 * Graph not configured). The add-user dialog catches this and falls back to
 * manual e-mail entry.
 */
export class DirectoryUnavailableError extends Error {
  constructor() {
    super('DIRECTORY_UNAVAILABLE');
  }
}

function unwrap<T>(response: ApiResponse<T>, errorMsg: string): T {
  if (response.error === AUTH_ERROR) throw new SessionExpiredError();
  if (response.success) return response.data as T;
  // Carry the HTTP status so callers can branch (e.g. 404 "not found in
  // registry" vs 502 "registry down").
  const error = new Error(response.error || errorMsg) as Error & { status?: number };
  error.status = response.status;
  throw error;
}

function unwrapVoid(response: ApiResponse<void>, errorMsg: string): void {
  if (response.error === AUTH_ERROR) throw new SessionExpiredError();
  if (!response.success) throw new Error(response.error || errorMsg);
}

export function useTagsApi() {
  const { get, post, put, delete: del } = useApi();

  const getTags = useCallback(
    async (nip: string, module?: TagModule): Promise<Tag[]> => {
      const query = module ? `?module=${module}` : '';
      return unwrap(
        await get<Tag[]>(`/companies/${nip}/tags${query}`),
        'Failed to fetch tags',
      );
    },
    [get],
  );

  const createTag = useCallback(
    async (nip: string, dto: CreateTagDto): Promise<Tag> => {
      return unwrap(
        await post<Tag>(`/companies/${nip}/tags`, dto),
        'Failed to create tag',
      );
    },
    [post],
  );

  const updateTag = useCallback(
    async (nip: string, id: string, dto: UpdateTagDto): Promise<Tag> => {
      return unwrap(
        await put<Tag>(`/companies/${nip}/tags/${id}`, dto),
        'Failed to update tag',
      );
    },
    [put],
  );

  const deleteTag = useCallback(
    async (nip: string, id: string): Promise<void> => {
      unwrapVoid(
        await del<void>(`/companies/${nip}/tags/${id}`),
        'Failed to delete tag',
      );
    },
    [del],
  );

  return { getTags, createTag, updateTag, deleteTag };
}

// ---- Users admin API (Task B6) ----
// These shapes mirror apps/backend/src/modules/users/users-admin.dto.ts and
// users-admin.types.ts exactly. They live here (not in shared-types) because
// the backend module deliberately keeps its admin DTOs local to the module.

export interface ModuleGrantInput {
  module: PermissionModule;
  /** Only meaningful for 'expense'/'sales' — restricts visibility to these tag ids. */
  tagIds?: string[];
}

// ---- Entra directory (user picker) ----
// Mirrors apps/backend/src/modules/graph/graph-directory.service.ts.

export interface DirectoryUser {
  entraOid: string;
  displayName: string | null;
  email: string;
  jobTitle: string | null;
  department: string | null;
}

export interface DirectoryManager {
  entraOid: string;
  displayName: string | null;
  email: string | null;
}

export interface DirectoryUserWithManager extends DirectoryUser {
  /** null = no manager assigned in Entra. */
  manager: DirectoryManager | null;
}

/**
 * Entra directory profile fields imported from Microsoft Graph; all null for
 * local accounts and for entra accounts not yet synced.
 */
export interface UserDirectoryFields {
  jobTitle: string | null;
  department: string | null;
  managerEmail: string | null;
  managerEntraOid: string | null;
  managerDisplayName: string | null;
  /** ISO 8601 timestamp of the last successful Graph refresh. */
  directorySyncedAt: string | null;
}

export interface CreateUserDto {
  email: string;
  displayName?: string;
  /**
   * Entra object id picked from the directory (authProvider 'entra' only).
   * When set, the backend imports email/displayName/jobTitle/department/
   * manager from Graph — the Graph email is authoritative.
   */
  entraOid?: string;
  role: UserRoleName;
  companyNip: string;
  /**
   * 'entra' = Microsoft (AAD) sign-in: no password, account links up on the
   * user's first login. 'local' (backend default) = password account + invite.
   */
  authProvider?: 'entra' | 'local';
  /** Only used when role === 'user'. */
  modules?: ModuleGrantInput[];
  /**
   * Local accounts: default true (invite with temp password). Entra accounts:
   * default false (opt-in, password-free access notification).
   */
  sendInvite?: boolean;
}

export interface AddRoleDto {
  role: UserRoleName;
  /** Only omit for role 'pracownik' (unassigned placeholder, superadmin-only). */
  companyNip?: string | null;
}

export interface ReplaceGrantsDto {
  companyNip: string;
  /** Full replacement set of module grants for this user+company. */
  modules: ModuleGrantInput[];
}

export interface UpdateUserDto {
  isActive?: boolean;
  displayName?: string;
}

export interface UserAdminRole {
  id: string;
  role: UserRoleName;
  companyNip: string | null;
}

export interface UserAdminGrant {
  id: string;
  companyNip: string;
  module: PermissionModule;
  tagId: string | null;
}

export interface UserAdminListItem extends UserDirectoryFields {
  id: string;
  email: string;
  displayName: string | null;
  authProvider: string;
  isActive: boolean;
  mustChangePassword: boolean;
  roles: UserAdminRole[];
  grants: UserAdminGrant[];
}

export interface CreateUserResult extends UserDirectoryFields {
  id: string;
  email: string;
  displayName: string | null;
  authProvider: string;
  isActive: boolean;
  mustChangePassword: boolean;
  /** Only present when the caller passed sendInvite: false. */
  temporaryPassword?: string;
}

/** Mirrors DirectorySyncSummary in apps/backend/src/modules/users/directory-sync.service.ts. */
export interface DirectorySyncSummary {
  scanned: number;
  updated: number;
  unchanged: number;
  backfilled: number;
  missing: number;
  failed: number;
  skipped: boolean;
  /** Employee auto-provisioning counts for app users (HR foundation). */
  employees: {
    created: number;
    linked: number;
    managerResolved: number;
  };
  /**
   * Directory-driven employee provisioning: every enabled Entra Member of a
   * mapped e-mail domain gets an Employee record, app account or not.
   */
  directory: {
    directoryMembers: number;
    employeesCreated: number;
    employeesUpdated: number;
    managersResolved: number;
    /** Directory-sourced employees no longer in Entra (kept — HR decides). */
    stale: number;
  };
}

/**
 * Thrown by POST /users/directory/sync when the backend answers
 * 409 DIRECTORY_SYNC_RUNNING — a sync (the cron or another manual trigger)
 * is already in progress.
 */
export class DirectorySyncRunningError extends Error {
  constructor() {
    super('DIRECTORY_SYNC_RUNNING');
  }
}

export function useUsersApi() {
  const { get, post, put, patch, delete: del } = useApi();

  const listUsers = useCallback(async (): Promise<UserAdminListItem[]> => {
    return unwrap(await get<UserAdminListItem[]>('/users'), 'Failed to fetch users');
  }, [get]);

  const createUser = useCallback(
    async (dto: CreateUserDto): Promise<CreateUserResult> => {
      const response = await post<CreateUserResult>('/users', dto);
      if (response.error === AUTH_ERROR) throw new SessionExpiredError();
      if (!response.success) {
        throw new Error(response.error || 'Failed to create user');
      }
      return response.data as CreateUserResult;
    },
    [post],
  );

  const addRole = useCallback(
    async (userId: string, dto: AddRoleDto): Promise<void> => {
      unwrapVoid(await post<void>(`/users/${userId}/roles`, dto), 'Failed to add role');
    },
    [post],
  );

  const removeRole = useCallback(
    async (userId: string, roleId: string): Promise<void> => {
      unwrapVoid(await del<void>(`/users/${userId}/roles/${roleId}`), 'Failed to remove role');
    },
    [del],
  );

  const replaceGrants = useCallback(
    async (userId: string, dto: ReplaceGrantsDto): Promise<void> => {
      unwrapVoid(await put<void>(`/users/${userId}/grants`, dto), 'Failed to update permissions');
    },
    [put],
  );

  const updateUser = useCallback(
    async (userId: string, dto: UpdateUserDto): Promise<UserAdminListItem> => {
      return unwrap(await patch<UserAdminListItem>(`/users/${userId}`, dto), 'Failed to update user');
    },
    [patch],
  );

  // 503 DIRECTORY_UNAVAILABLE surfaces as the response error message — map it
  // to the typed error so the dialog can fall back to manual entry.
  const unwrapDirectory = <T,>(response: ApiResponse<T>, errorMsg: string): T => {
    if (response.error === AUTH_ERROR) throw new SessionExpiredError();
    if (response.error === 'DIRECTORY_UNAVAILABLE') throw new DirectoryUnavailableError();
    if (response.success) return response.data as T;
    throw new Error(response.error || errorMsg);
  };

  const searchDirectory = useCallback(
    async (query: string): Promise<DirectoryUser[]> => {
      return unwrapDirectory(
        await get<DirectoryUser[]>(`/users/directory?q=${encodeURIComponent(query)}`),
        'Failed to search the directory',
      );
    },
    [get],
  );

  const getDirectoryUser = useCallback(
    async (entraOid: string): Promise<DirectoryUserWithManager> => {
      return unwrapDirectory(
        await get<DirectoryUserWithManager>(`/users/directory/${encodeURIComponent(entraOid)}`),
        'Failed to fetch the directory user',
      );
    },
    [get],
  );

  // Manual trigger for the nightly Entra directory re-sync (superadmin only).
  // 409 DIRECTORY_SYNC_RUNNING and 503 DIRECTORY_UNAVAILABLE surface as typed
  // errors so the UI can toast a specific message for each.
  const triggerDirectorySync = useCallback(async (): Promise<DirectorySyncSummary> => {
    const response = await post<DirectorySyncSummary>('/users/directory/sync', {});
    if (response.error === AUTH_ERROR) throw new SessionExpiredError();
    if (response.error === 'DIRECTORY_UNAVAILABLE') throw new DirectoryUnavailableError();
    if (response.error === 'DIRECTORY_SYNC_RUNNING') throw new DirectorySyncRunningError();
    if (response.success) return response.data as DirectorySyncSummary;
    throw new Error(response.error || 'Failed to sync the directory');
  }, [post]);

  return {
    listUsers,
    createUser,
    addRole,
    removeRole,
    replaceGrants,
    updateUser,
    searchDirectory,
    getDirectoryUser,
    triggerDirectorySync,
  };
}

// ---- Global API applications (pap_… tokens, superadmin only) ----
// Mirrors apps/backend/src/modules/api-apps/api-apps-admin.controller.ts.
// Create/regenerate answer with the plaintext token exactly once — the list
// endpoint only ever returns the stored prefix.
export function useApiAppsApi() {
  const { get, post, put, delete: del } = useApi();
  const base = '/admin/api-applications';

  const listApiApps = useCallback(
    async (): Promise<ApiApplicationDto[]> =>
      unwrap(await get<ApiApplicationDto[]>(base), 'Failed to fetch API applications'),
    [get],
  );

  const createApiApp = useCallback(
    async (body: {
      name: string;
      description?: string | null;
      scopes: string[];
      companyNip?: string | null;
      expiresAt?: string | null;
    }): Promise<{ token: string; dto: ApiApplicationDto }> =>
      unwrap(
        await post<{ token: string; dto: ApiApplicationDto }>(base, body),
        'Failed to create API application',
      ),
    [post],
  );

  const updateApiApp = useCallback(
    async (
      id: string,
      patch: {
        name?: string;
        description?: string | null;
        scopes?: string[];
        companyNip?: string | null;
        expiresAt?: string | null;
        active?: boolean;
      },
    ): Promise<ApiApplicationDto> =>
      unwrap(await put<ApiApplicationDto>(`${base}/${id}`, patch), 'Failed to update API application'),
    [put],
  );

  const regenerateApiAppToken = useCallback(
    async (id: string): Promise<{ token: string; dto: ApiApplicationDto }> =>
      unwrap(
        await post<{ token: string; dto: ApiApplicationDto }>(`${base}/${id}/regenerate-token`, {}),
        'Failed to regenerate token',
      ),
    [post],
  );

  const deleteApiApp = useCallback(
    async (id: string): Promise<void> =>
      unwrapVoid(await del<void>(`${base}/${id}`), 'Failed to delete API application'),
    [del],
  );

  return { listApiApps, createApiApp, updateApiApp, regenerateApiAppToken, deleteApiApp };
}

/** What POST /companies/:nip/logo answers with. */
export interface CompanyLogo {
  logoUrl: string | null;
  logoInk: LogoInk;
}

export function useCompaniesApi() {
  const { get, post, put, delete: del, postFormData } = useApi();

  const uploadCompanyLogo = useCallback(
    async (nip: string, file: File): Promise<CompanyLogo> => {
      const fd = new FormData();
      fd.append('file', file);
      return unwrap(
        await postFormData<CompanyLogo>(`/companies/${nip}/logo`, fd),
        'Failed to upload the logo',
      );
    },
    [postFormData],
  );

  const deleteCompanyLogo = useCallback(
    async (nip: string): Promise<void> => {
      unwrapVoid(await del<void>(`/companies/${nip}/logo`), 'Failed to remove the logo');
    },
    [del],
  );

  const getCompanies = useCallback(async (): Promise<CompanyListItem[]> => {
    return unwrap(await get<CompanyListItem[]>('/companies'), 'Failed to fetch companies');
  }, [get]);

  const getCompanyDetail = useCallback(
    async (nip: string): Promise<CompanyDetail> => {
      return unwrap(await get<CompanyDetail>(`/companies/${nip}/detail`), 'Failed to fetch company details');
    },
    [get],
  );

  const updateCompany = useCallback(
    async (nip: string, dto: UpdateCompanyDto): Promise<void> => {
      unwrapVoid(await put<void>(`/companies/${nip}`, dto), 'Failed to update company');
    },
    [put],
  );

  const createCompany = useCallback(
    async (dto: CreateCompanyDto): Promise<{ nip: string; name: string | null }> => {
      const response = await post<{ nip: string; name: string | null }>('/companies', dto);
      if (response.error === AUTH_ERROR) throw new SessionExpiredError();
      if (!response.success) {
        if (response.error?.toLowerCase().includes('already exists')) {
          throw new DuplicateNipError(response.error);
        }
        throw new Error(response.error || 'Failed to create company');
      }
      return response.data as { nip: string; name: string | null };
    },
    [post],
  );

  const deleteCompany = useCallback(
    async (nip: string, mode: DeleteCompanyMode): Promise<DeleteCompanyResult> => {
      return unwrap(
        await del<DeleteCompanyResult>(`/companies/${nip}?mode=${mode}`),
        'Failed to delete company',
      );
    },
    [del],
  );

  const getSalesSummary = useCallback(
    async (nip: string, year: number): Promise<SalesSummary> => {
      return unwrap(
        await get<SalesSummary>(`/companies/${nip}/sales/summary?year=${year}`),
        'Failed to fetch sales summary',
      );
    },
    [get],
  );

  return {
    getCompanies,
    getCompanyDetail,
    updateCompany,
    createCompany,
    deleteCompany,
    getSalesSummary,
    uploadCompanyLogo,
    deleteCompanyLogo,
  };
}

export function useDashboardApi() {
  const { get } = useApi();

  const getDashboard = useCallback(
    async (companyNip: string, year: number): Promise<DashboardResponse> => {
      return unwrap(
        await get<DashboardResponse>(`/companies/${companyNip}/dashboard?year=${year}`),
        'Failed to fetch dashboard data',
      );
    },
    [get],
  );

  const getFinancialOverview = useCallback(
    async (companyNip: string, year: number): Promise<FinancialOverview> => {
      return unwrap(
        await get<FinancialOverview>(`/companies/${companyNip}/analytics/financial?year=${year}`),
        'Failed to fetch financial overview',
      );
    },
    [get],
  );

  return { getDashboard, getFinancialOverview };
}

export function useContractorsApi() {
  const { get, put, patch, post, delete: del, postFormData, getBlob } = useApi();

  const getContractors = useCallback(
    async (companyNip: string): Promise<ContractorListItem[]> => {
      return unwrap(
        await get<ContractorListItem[]>(`/companies/${companyNip}/contractors`),
        'Failed to fetch contractors',
      );
    },
    [get],
  );

  const getContractor = useCallback(
    async (companyNip: string, contractorId: string): Promise<ContractorDetail> => {
      return unwrap(
        await get<ContractorDetail>(`/companies/${companyNip}/contractors/${contractorId}`),
        'Failed to fetch contractor',
      );
    },
    [get],
  );

  const updateContractor = useCallback(
    async (companyNip: string, contractorId: string, data: UpdateContractorDto): Promise<void> => {
      unwrapVoid(
        await put<void>(`/companies/${companyNip}/contractors/${contractorId}`, data),
        'Failed to update contractor',
      );
    },
    [put],
  );

  const updateContractorTags = useCallback(
    async (companyNip: string, contractorId: string, tagIds: string[]): Promise<void> => {
      unwrapVoid(
        await put<void>(`/companies/${companyNip}/contractors/${contractorId}/tags`, { tagIds }),
        'Failed to update contractor tags',
      );
    },
    [put],
  );

  const getContractorDashboard = useCallback(
    async (companyNip: string, contractorId: string, year: number): Promise<ContractorDashboardResponse> => {
      return unwrap(
        await get<ContractorDashboardResponse>(
          `/companies/${companyNip}/contractors/${contractorId}/dashboard?year=${year}`,
        ),
        'Failed to fetch contractor dashboard',
      );
    },
    [get],
  );

  // Documents (typed archive: signed umowy/NDA scans etc.)
  const listContractorDocuments = useCallback(
    async (companyNip: string, contractorId: string): Promise<ContractorDocument[]> =>
      unwrap(
        await get<ContractorDocument[]>(`/companies/${companyNip}/contractors/${contractorId}/documents`),
        'Failed to load documents',
      ),
    [get],
  );

  const addContractorDocument = useCallback(
    async (companyNip: string, contractorId: string, formData: FormData): Promise<ContractorDocument> =>
      unwrap(
        await postFormData<ContractorDocument>(
          `/companies/${companyNip}/contractors/${contractorId}/documents`,
          formData,
        ),
        'Failed to upload document',
      ),
    [postFormData],
  );

  // Metadata patch (label / dates / note) — PUT-as-patch: an unsent key stays,
  // a key sent as null clears. Changing expiryDate restarts the reminder
  // ladder server-side.
  const updateContractorDocument = useCallback(
    async (
      companyNip: string,
      contractorId: string,
      docId: string,
      dto: UpdateContractorDocumentDto,
    ): Promise<ContractorDocument> =>
      unwrap(
        await patch<ContractorDocument>(
          `/companies/${companyNip}/contractors/${contractorId}/documents/${docId}`,
          dto,
        ),
        'Failed to update document',
      ),
    [patch],
  );

  const downloadContractorDocument = useCallback(
    async (companyNip: string, contractorId: string, docId: string, fileName: string): Promise<void> =>
      downloadViaApi(
        getBlob,
        `/companies/${companyNip}/contractors/${contractorId}/documents/${docId}/file`,
        fileName,
      ),
    [getBlob],
  );

  const removeContractorDocument = useCallback(
    async (companyNip: string, contractorId: string, docId: string): Promise<void> =>
      unwrapVoid(
        await del<void>(`/companies/${companyNip}/contractors/${contractorId}/documents/${docId}`),
        'Failed to delete document',
      ),
    [del],
  );

  // Return routing (documents plan 4): the signed copy routed back for a
  // generated contractor document, and HR's confirmation of it.
  const downloadContractorReturned = useCallback(
    async (companyNip: string, contractorId: string, docId: string, fileName: string): Promise<void> =>
      downloadViaApi(
        getBlob,
        `/companies/${companyNip}/contractors/${contractorId}/documents/${docId}/returned-file`,
        fileName,
      ),
    [getBlob],
  );

  const verifyContractorDocumentSignature = useCallback(
    async (companyNip: string, contractorId: string, docId: string): Promise<SignatureVerification> =>
      unwrap(
        await post<SignatureVerification>(
          `/companies/${companyNip}/contractors/${contractorId}/documents/${docId}/verify-signature`,
          {},
        ),
        'Failed to verify the signature',
      ),
    [post],
  );

  const confirmContractorReturn = useCallback(
    async (companyNip: string, contractorId: string, docId: string): Promise<ContractorDocument> =>
      unwrap(
        await post<ContractorDocument>(
          `/companies/${companyNip}/contractors/${contractorId}/documents/${docId}/confirm-return`,
          {},
        ),
        'Failed to confirm the returned document',
      ),
    [post],
  );

  return {
    getContractors, getContractor, updateContractor, updateContractorTags, getContractorDashboard,
    listContractorDocuments, addContractorDocument, updateContractorDocument,
    downloadContractorDocument, removeContractorDocument,
    downloadContractorReturned, verifyContractorDocumentSignature, confirmContractorReturn,
  };
}

export function useInvoicesApi() {
  const { get, put, post, delete: del, getBlob } = useApi();

  const getInvoices = useCallback(
    async (
      companyNip: string,
      params: Omit<InvoiceFilterParams, 'companyNip'>,
    ): Promise<PaginatedResponse<InvoiceListItem>> => {
      const queryParams = new URLSearchParams();

      if (params.search) queryParams.set('search', params.search);
      if (params.mppFilter) queryParams.set('mppFilter', params.mppFilter);
      if (params.dateType) queryParams.set('dateType', params.dateType);
      if (params.dateFrom) queryParams.set('dateFrom', params.dateFrom);
      if (params.dateTo) queryParams.set('dateTo', params.dateTo);
      if (params.tagIds?.length) queryParams.set('tagIds', params.tagIds.join(','));
      if (params.status) queryParams.set('status', params.status);
      if (params.source && params.source !== 'all') queryParams.set('source', params.source);
      if (params.direction) queryParams.set('direction', params.direction);
      if (params.settlement) queryParams.set('settlement', params.settlement);
      // NOTE: hasNotes=false is meaningful ("bez opisu") — serialize explicit
      // booleans only, never drop `false` with a truthiness check.
      if (params.hasNotes !== undefined) queryParams.set('hasNotes', String(params.hasNotes));
      if (params.projectId) queryParams.set('projectId', params.projectId);
      if (params.reimbursementFilter && params.reimbursementFilter !== 'all') {
        queryParams.set('reimbursementFilter', params.reimbursementFilter);
      }
      if (params.page) queryParams.set('page', params.page.toString());
      if (params.pageSize) queryParams.set('pageSize', params.pageSize.toString());
      if (params.sortBy) queryParams.set('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);

      const query = queryParams.toString();
      const url = `/companies/${companyNip}/invoices${query ? `?${query}` : ''}`;

      return unwrap(await get<PaginatedResponse<InvoiceListItem>>(url), 'Failed to fetch invoices');
    },
    [get],
  );

  const updateInvoiceTags = useCallback(
    async (companyNip: string, invoiceId: string, tagIds: string[]): Promise<void> => {
      unwrapVoid(
        await put<void>(`/companies/${companyNip}/invoices/${invoiceId}/tags`, { tagIds }),
        'Failed to update invoice tags',
      );
    },
    [put],
  );

  const updateInvoiceStatus = useCallback(
    async (companyNip: string, invoiceId: string, status: InvoiceStatus): Promise<void> => {
      unwrapVoid(
        await put<void>(`/companies/${companyNip}/invoices/${invoiceId}/status`, { status }),
        'Failed to update invoice status',
      );
    },
    [put],
  );

  const bulkUpdateTags = useCallback(
    async (
      companyNip: string,
      invoiceIds: string[],
      addTagIds?: string[],
      removeTagIds?: string[],
    ): Promise<void> => {
      unwrapVoid(
        await post<void>(`/companies/${companyNip}/invoices/bulk/tags`, { invoiceIds, addTagIds, removeTagIds }),
        'Failed to bulk update tags',
      );
    },
    [post],
  );

  const bulkUpdateStatus = useCallback(
    async (companyNip: string, invoiceIds: string[], status: InvoiceStatus): Promise<void> => {
      unwrapVoid(
        await post<void>(`/companies/${companyNip}/invoices/bulk/status`, { invoiceIds, status }),
        'Failed to bulk update status',
      );
    },
    [post],
  );

  const bulkDeleteInvoices = useCallback(
    async (companyNip: string, invoiceIds: string[]): Promise<BulkDeleteInvoicesResponse> => {
      return unwrap(
        await post<BulkDeleteInvoicesResponse>(`/companies/${companyNip}/invoices/bulk/delete`, { invoiceIds }),
        'Failed to bulk delete invoices',
      );
    },
    [post],
  );

  /**
   * Bulk MANUAL whole-invoice project assignment (projectId null clears).
   * Ids not owned by the company are silently skipped server-side; the
   * response carries the actually-updated count.
   */
  const bulkAssignProject = useCallback(
    async (
      companyNip: string,
      invoiceIds: string[],
      projectId: string | null,
    ): Promise<BulkAssignInvoiceProjectResult> => {
      return unwrap(
        await post<BulkAssignInvoiceProjectResult>(`/companies/${companyNip}/invoices/bulk/project`, {
          invoiceIds,
          projectId,
        }),
        'Failed to bulk assign project',
      );
    },
    [post],
  );

  const getInvoice = useCallback(
    async (companyNip: string, invoiceId: string): Promise<InvoiceDetail> => {
      return unwrap(
        await get<InvoiceDetail>(`/companies/${companyNip}/invoices/${invoiceId}`),
        'Failed to fetch invoice',
      );
    },
    [get],
  );

  const updateInvoice = useCallback(
    async (companyNip: string, invoiceId: string, data: UpdateInvoiceDto): Promise<void> => {
      unwrapVoid(
        await put<void>(`/companies/${companyNip}/invoices/${invoiceId}`, data),
        'Failed to update invoice',
      );
    },
    [put],
  );

  const recheckVat = useCallback(
    async (companyNip: string, invoiceId: string): Promise<VatPayerData> => {
      return unwrap(
        await post<VatPayerData>(`/companies/${companyNip}/invoices/${invoiceId}/vat-recheck`, {}),
        'Failed to recheck VAT status',
      );
    },
    [post],
  );

  const deleteInvoice = useCallback(
    async (companyNip: string, invoiceId: string): Promise<void> => {
      unwrapVoid(
        await del<void>(`/companies/${companyNip}/invoices/${invoiceId}`),
        'Failed to delete invoice',
      );
    },
    [del],
  );

  const getKsefPdf = useCallback(
    async (companyNip: string, invoiceId: string): Promise<Blob> => {
      return getBlob(`/companies/${companyNip}/invoices/${invoiceId}/ksef-pdf`);
    },
    [getBlob],
  );

  // Streams the stored PDF through our API (Content-Disposition: attachment)
  // and saves it — replaces the presigned-URL + new-tab flow (see utils/download.ts).
  const downloadInvoicePdf = useCallback(
    async (companyNip: string, invoiceId: string, fileName: string): Promise<void> =>
      downloadViaApi(getBlob, `/companies/${companyNip}/invoices/${invoiceId}/pdf`, fileName),
    [getBlob],
  );

  const assignInvoices = useCallback(
    async (sourceCompanyNip: string, invoiceIds: string[], targetCompanyNip: string): Promise<AssignInvoicesResponse> => {
      return unwrap(
        await post<AssignInvoicesResponse>(`/companies/${sourceCompanyNip}/invoices/assign`, { invoiceIds, targetCompanyNip }),
        'Failed to assign invoices',
      );
    },
    [post],
  );

  return {
    getInvoices,
    getInvoice,
    updateInvoice,
    updateInvoiceTags,
    updateInvoiceStatus,
    bulkUpdateTags,
    bulkUpdateStatus,
    bulkDeleteInvoices,
    bulkAssignProject,
    recheckVat,
    deleteInvoice,
    getKsefPdf,
    downloadInvoicePdf,
    assignInvoices,
  };
}

// ---- Settlement (Task A3): sale-invoice payment history + receivables ----
export function useSettlementApi() {
  const { get, post, delete: del, postRaw } = useApi();

  const addPayment = useCallback(
    async (companyNip: string, invoiceId: string, dto: AddInvoicePaymentDto): Promise<AddInvoicePaymentResponse> => {
      return unwrap(
        await post<AddInvoicePaymentResponse>(`/companies/${companyNip}/invoices/${invoiceId}/payments`, dto),
        'Failed to register payment',
      );
    },
    [post],
  );

  const listPayments = useCallback(
    async (companyNip: string, invoiceId: string): Promise<InvoicePaymentItem[]> => {
      return unwrap(
        await get<InvoicePaymentItem[]>(`/companies/${companyNip}/invoices/${invoiceId}/payments`),
        'Failed to fetch payments',
      );
    },
    [get],
  );

  const deletePayment = useCallback(
    async (companyNip: string, invoiceId: string, paymentId: string): Promise<void> => {
      unwrapVoid(
        await del<void>(`/companies/${companyNip}/invoices/${invoiceId}/payments/${paymentId}`),
        'Failed to delete payment',
      );
    },
    [del],
  );

  const getReceivables = useCallback(
    async (companyNip: string): Promise<ReceivableCustomer[]> => {
      return unwrap(
        await get<ReceivableCustomer[]>(`/companies/${companyNip}/receivables`),
        'Failed to fetch receivables',
      );
    },
    [get],
  );

  // Customer statement PDF (wyciąg / potwierdzenie salda).
  const getStatementCustomers = useCallback(
    async (companyNip: string): Promise<SalesStatementCustomer[]> => {
      return unwrap(
        await get<SalesStatementCustomer[]>(`/companies/${companyNip}/sales/statement/customers`),
        'Failed to fetch statement customers',
      );
    },
    [get],
  );

  // postRaw throws Error(server message) on a non-2xx, so the toast shows it.
  const downloadSalesStatement = useCallback(
    async (companyNip: string, body: SalesStatementRequest): Promise<Blob> => {
      return postRaw(`/companies/${companyNip}/sales/statement/pdf`, body);
    },
    [postRaw],
  );

  return {
    addPayment,
    listPayments,
    deletePayment,
    getReceivables,
    getStatementCustomers,
    downloadSalesStatement,
  };
}

// ---- Combined payments (płatność łączona): one bank/card transaction covering several purchase invoices ----
export function useCombinedPaymentsApi() {
  const { get, post, delete: del } = useApi();

  const searchCombinations = useCallback(
    async (companyNip: string, request: CombinedPaymentSearchRequest): Promise<CombinedPaymentSearchResult> => {
      return unwrap(
        await post<CombinedPaymentSearchResult>(`/companies/${companyNip}/combined-payments/search`, request),
        'Failed to search matching invoices',
      );
    },
    [post],
  );

  const createCombinedPayment = useCallback(
    async (companyNip: string, dto: CreateCombinedPaymentDto): Promise<CombinedPaymentDetail> => {
      return unwrap(
        await post<CombinedPaymentDetail>(`/companies/${companyNip}/combined-payments`, dto),
        'Failed to create combined payment',
      );
    },
    [post],
  );

  const getCombinedPayment = useCallback(
    async (companyNip: string, id: string): Promise<CombinedPaymentDetail> => {
      return unwrap(
        await get<CombinedPaymentDetail>(`/companies/${companyNip}/combined-payments/${id}`),
        'Failed to fetch combined payment',
      );
    },
    [get],
  );

  const deleteCombinedPayment = useCallback(
    async (companyNip: string, id: string): Promise<void> => {
      unwrapVoid(
        await del<void>(`/companies/${companyNip}/combined-payments/${id}`),
        'Failed to delete combined payment',
      );
    },
    [del],
  );

  // ---- Sales side ("Dopasuj wpłatę") ----

  const listSalesMatchCustomers = useCallback(
    async (companyNip: string): Promise<SalesCombinedPaymentCustomer[]> => {
      return unwrap(
        await get<SalesCombinedPaymentCustomer[]>(`/companies/${companyNip}/combined-payments/sales/customers`),
        'Failed to fetch customers',
      );
    },
    [get],
  );

  const searchSalesCombinations = useCallback(
    async (companyNip: string, request: SalesCombinedPaymentSearchRequest): Promise<CombinedPaymentSearchResult> => {
      return unwrap(
        await post<CombinedPaymentSearchResult>(`/companies/${companyNip}/combined-payments/sales/search`, request),
        'Failed to search matching invoices',
      );
    },
    [post],
  );

  const createSalesCombinedPayment = useCallback(
    async (companyNip: string, dto: CreateSalesCombinedPaymentDto): Promise<CombinedPaymentDetail> => {
      return unwrap(
        await post<CombinedPaymentDetail>(`/companies/${companyNip}/combined-payments/sales`, dto),
        'Failed to create combined payment',
      );
    },
    [post],
  );

  return {
    searchCombinations,
    createCombinedPayment,
    getCombinedPayment,
    deleteCombinedPayment,
    listSalesMatchCustomers,
    searchSalesCombinations,
    createSalesCombinedPayment,
  };
}

// ---- Invoice notes (Task A3): accounting descriptions on invoices ----
export function useNotesApi() {
  const { get, post, patch, delete: del } = useApi();

  const listNotes = useCallback(
    async (companyNip: string, invoiceId: string): Promise<InvoiceNote[]> => {
      return unwrap(
        await get<InvoiceNote[]>(`/companies/${companyNip}/invoices/${invoiceId}/notes`),
        'Failed to fetch notes',
      );
    },
    [get],
  );

  const addNote = useCallback(
    async (companyNip: string, invoiceId: string, body: string): Promise<InvoiceNote> => {
      return unwrap(
        await post<InvoiceNote>(`/companies/${companyNip}/invoices/${invoiceId}/notes`, { body }),
        'Failed to add note',
      );
    },
    [post],
  );

  const updateNote = useCallback(
    async (companyNip: string, invoiceId: string, noteId: string, body: string): Promise<InvoiceNote> => {
      return unwrap(
        await patch<InvoiceNote>(`/companies/${companyNip}/invoices/${invoiceId}/notes/${noteId}`, { body }),
        'Failed to update note',
      );
    },
    [patch],
  );

  const deleteNote = useCallback(
    async (companyNip: string, invoiceId: string, noteId: string): Promise<void> => {
      unwrapVoid(
        await del<void>(`/companies/${companyNip}/invoices/${invoiceId}/notes/${noteId}`),
        'Failed to delete note',
      );
    },
    [del],
  );

  return { listNotes, addNote, updateNote, deleteNote };
}

// ---- Projects (Task B3): per-company P&L projects + invoice/line assignment ----
export function useProjectsApi() {
  const { get, post, put } = useApi();

  /** Project list with computed P&L. Requires the 'projects' module; 403 for tag-restricted users (aggregate). */
  const listProjects = useCallback(
    async (companyNip: string): Promise<ProjectListItem[]> => {
      return unwrap(
        await get<ProjectListItem[]>(`/companies/${companyNip}/projects`),
        'Failed to fetch projects',
      );
    },
    [get],
  );

  /**
   * Light rows (no amounts) for assignment selectors/filters — available to
   * anyone with expense/sales/projects, incl. tag-restricted users.
   */
  const listProjectOptions = useCallback(
    async (companyNip: string): Promise<ProjectOption[]> => {
      return unwrap(
        await get<ProjectOption[]>(`/companies/${companyNip}/projects/options`),
        'Failed to fetch project options',
      );
    },
    [get],
  );

  const getProject = useCallback(
    async (companyNip: string, projectId: string): Promise<Project> => {
      return unwrap(
        await get<Project>(`/companies/${companyNip}/projects/${projectId}`),
        'Failed to fetch project',
      );
    },
    [get],
  );

  const createProject = useCallback(
    async (companyNip: string, dto: CreateProjectDto): Promise<Project> => {
      return unwrap(
        await post<Project>(`/companies/${companyNip}/projects`, dto),
        'Failed to create project',
      );
    },
    [post],
  );

  /** Update; passing status: 'closed' / 'active' closes / reopens the project. */
  const updateProject = useCallback(
    async (companyNip: string, projectId: string, dto: UpdateProjectDto): Promise<Project> => {
      return unwrap(
        await put<Project>(`/companies/${companyNip}/projects/${projectId}`, dto),
        'Failed to update project',
      );
    },
    [put],
  );

  const getProjectAnalytics = useCallback(
    async (companyNip: string, projectId: string): Promise<ProjectAnalytics> => {
      return unwrap(
        await get<ProjectAnalytics>(`/companies/${companyNip}/projects/${projectId}/analytics`),
        'Failed to fetch project analytics',
      );
    },
    [get],
  );

  /**
   * Whole-invoice assignment — null clears it. Line-level assignments always
   * WIN over this value in attribution, so the UI disables this control while
   * any line of the invoice carries its own project.
   */
  const assignInvoiceProject = useCallback(
    async (companyNip: string, invoiceId: string, projectId: string | null): Promise<void> => {
      unwrapVoid(
        await put<void>(`/companies/${companyNip}/invoices/${invoiceId}/project`, { projectId }),
        'Failed to assign project',
      );
    },
    [put],
  );

  /** Bulk per-line assignment — replaces the assignment of exactly the given lines. */
  const assignLineItemProjects = useCallback(
    async (
      companyNip: string,
      invoiceId: string,
      assignments: LineItemProjectAssignment[],
    ): Promise<void> => {
      unwrapVoid(
        await put<void>(`/companies/${companyNip}/invoices/${invoiceId}/line-items/projects`, {
          assignments,
        }),
        'Failed to assign line item projects',
      );
    },
    [put],
  );

  /** The project's rule tag ids (admin/ksiegowy/superadmin only, like PUT). */
  const getTagRules = useCallback(
    async (companyNip: string, projectId: string): Promise<ProjectTagRulesDto> => {
      return unwrap(
        await get<ProjectTagRulesDto>(`/companies/${companyNip}/projects/${projectId}/tag-rules`),
        'Failed to fetch tag rules',
      );
    },
    [get],
  );

  /** Full replacement of the rule set — an empty array clears all rules. */
  const setTagRules = useCallback(
    async (companyNip: string, projectId: string, tagIds: string[]): Promise<ProjectTagRulesDto> => {
      return unwrap(
        await put<ProjectTagRulesDto>(`/companies/${companyNip}/projects/${projectId}/tag-rules`, { tagIds }),
        'Failed to save tag rules',
      );
    },
    [put],
  );

  /**
   * Retroactive rule application. dryRun=true (the backend default) only
   * counts; ONLY an explicit dryRun=false writes assignments.
   */
  const applyTagRules = useCallback(
    async (
      companyNip: string,
      projectId: string,
      dryRun: boolean,
    ): Promise<ProjectTagRuleApplyResult> => {
      return unwrap(
        await post<ProjectTagRuleApplyResult>(
          `/companies/${companyNip}/projects/${projectId}/tag-rules/apply?dryRun=${dryRun}`,
          {},
        ),
        'Failed to apply tag rules',
      );
    },
    [post],
  );

  return {
    listProjects,
    listProjectOptions,
    getProject,
    createProject,
    updateProject,
    getProjectAnalytics,
    assignInvoiceProject,
    assignLineItemProjects,
    getTagRules,
    setTagRules,
    applyTagRules,
  };
}

export function useExportsApi() {
  const { get, postRaw } = useApi();

  const generateMBankExport = useCallback(
    async (companyNip: string, request: MBankExportRequest): Promise<Blob> => {
      return postRaw(`/companies/${companyNip}/exports/mbank/pain00100109`, request);
    },
    [postRaw],
  );

  const generatePdfZipExport = useCallback(
    async (companyNip: string, invoiceIds: string[]): Promise<Blob> => {
      return postRaw(`/companies/${companyNip}/exports/pdf-zip`, { invoiceIds });
    },
    [postRaw],
  );

  const getReimbursementExportRecords = useCallback(
    async (companyNip: string): Promise<ReimbursementExportRecord[]> =>
      unwrap(
        await get<ReimbursementExportRecord[]>(`/companies/${companyNip}/exports/reimbursements`),
        'Failed to load reimbursements',
      ),
    [get],
  );

  return { generateMBankExport, generatePdfZipExport, getReimbursementExportRecords };
}

export function useRegistryApi() {
  const { get, post } = useApi();

  /** Government-registry lookup: Polish NIP → MF White List, EU VAT id → VIES. */
  const lookupRegistry = useCallback(
    async (id: string): Promise<RegistryCompanyData> => {
      return unwrap(
        await get<RegistryCompanyData>(`/registry/lookup/${encodeURIComponent(id)}`),
        'Failed to lookup company in the registry',
      );
    },
    [get],
  );

  /** Refresh a contractor from the registry; returns updated row + account comparison. */
  const refreshContractorRegistry = useCallback(
    async (companyNip: string, contractorId: string): Promise<ContractorRegistryRefreshResult> => {
      return unwrap(
        await post<ContractorRegistryRefreshResult>(
          `/companies/${companyNip}/contractors/${contractorId}/registry-refresh`,
          {},
        ),
        'Failed to refresh contractor from the registry',
      );
    },
    [post],
  );

  return { lookupRegistry, refreshContractorRegistry };
}

export function useFxApi() {
  const { get } = useApi();

  const getRates = useCallback(
    async (codes: string[], days = 30): Promise<FxRatesResponse> => {
      const params = new URLSearchParams({ codes: codes.join(','), days: String(days) });
      return unwrap(
        await get<FxRatesResponse>(`/fx/rates?${params.toString()}`),
        'Failed to fetch FX rates',
      );
    },
    [get],
  );

  return { getRates };
}

export interface UploadInvoiceResponse {
  status: 'ok';
  isInvoice: boolean;
  invoiceId: string | null;
  customerNip: string | null;
  vendorNip: string | null;
  vendorName: string | null;
  gross: number | null;
  currency: string | null;
  message: string;
}

export function useKsefApi() {
  const { get, put, post, delete: del, postFormData } = useApi();

  const getConfig = useCallback(
    async (nip: string): Promise<KsefConfig> => {
      return unwrap(await get<KsefConfig>(`/companies/${nip}/ksef/config`), 'Failed to fetch KSeF config');
    },
    [get],
  );

  const updateConfig = useCallback(
    async (nip: string, dto: UpdateKsefConfigDto): Promise<void> => {
      unwrapVoid(await put<void>(`/companies/${nip}/ksef/config`, dto), 'Failed to update KSeF config');
    },
    [put],
  );

  const uploadCertificate = useCallback(
    async (nip: string, file: File, password?: string, keyFile?: File): Promise<{ fingerprint: string }> => {
      const formData = new FormData();
      formData.append('certificate', file);
      if (password) {
        formData.append('password', password);
      }
      if (keyFile) {
        formData.append('keyFile', keyFile);
      }
      return unwrap(
        await postFormData<{ fingerprint: string }>(`/companies/${nip}/ksef/certificate`, formData),
        'Failed to upload certificate',
      );
    },
    [postFormData],
  );

  const removeCertificate = useCallback(
    async (nip: string): Promise<void> => {
      unwrapVoid(await del<void>(`/companies/${nip}/ksef/certificate`), 'Failed to remove certificate');
    },
    [del],
  );

  const testConnection = useCallback(
    async (nip: string): Promise<KsefTestConnectionResult> => {
      return unwrap(
        await post<KsefTestConnectionResult>(`/companies/${nip}/ksef/test-connection`, {}),
        'Failed to test KSeF connection',
      );
    },
    [post],
  );

  const triggerSync = useCallback(
    async (nip: string): Promise<KsefSyncResult> => {
      return unwrap(
        await post<KsefSyncResult>(`/companies/${nip}/ksef/sync`, {}),
        'Failed to trigger KSeF sync',
      );
    },
    [post],
  );

  return { getConfig, updateConfig, uploadCertificate, removeCertificate, testConnection, triggerSync };
}

// ==================== FISCAL (KSeF invoices + fiscal receipts) ====================
// Company admin panel surface — mirrors apps/backend/.../fiscal-admin.controller.ts
// route-for-route. Read-only invoice/receipt shapes are passthrough `object`s on
// the backend (FiscalInvoiceService/FiscalReceiptService.toApiResponse); the
// interfaces below mirror those projections field-for-field so callers get
// real typing without a shared-types round-trip.

export interface FiscalInvoiceListItem {
  uuid: string;
  status: FiscalInvoiceStatus;
  number: string;
  /** Środowisko KSeF zamrożone przy submicie; null dla faktur sprzed per-token env. */
  environment: KsefEnvironment | null;
  ksefNumber: string | null;
  ksefProcessedAt: string | null;
  verificationUrl: string | null;
  qrPngUrl: string | null;
  invoice: FiscalInvoiceInput;
  error: { message: string; attempts: number; nextRetryAt: string | null } | null;
  webhook: { status: string; attempts: number } | null;
  createdAt: string;
  confirmedAt: string | null;
}

export interface FiscalReceiptListItem {
  uuid: string;
  printerId: string | null;
  mode: string;
  status: FiscalReceiptStatus;
  jpkid: string | null;
  eDocument: { status: string; code: string | null } | null;
  fiscalizedAt: string | null;
  pdfUrl: string | null;
  receipt: FiscalReceiptInput;
  error: { message: string } | null;
  webhook: { status: string; attempts: number } | null;
  createdAt: string;
  confirmedAt: string | null;
}

/** Mirrors backend FiscalEventService's FiscalEventItem (pod-aligned field names). */
export interface FiscalEventItem {
  id: string;
  subjectUuid: string;
  at: string;
  kind: string;
  detailJson: Record<string, unknown> | null;
}

export interface CreateFiscalPrinterDto {
  code: string;
  name: string;
  dailyReportMode?: 'central' | 'agent';
  dailyReportTime?: string;
}

export interface UpdateFiscalPrinterDto {
  name?: string;
  dailyReportMode?: 'central' | 'agent';
  dailyReportTime?: string;
  active?: boolean;
}

export interface SeedNumberingCounterDto {
  prefix: string;
  year: number;
  month: number;
  lastSeq: number;
  /** Seria liczników jest per środowisko; pominięte = bieżące środowisko firmy. */
  environment?: KsefEnvironment;
}

export interface FiscalInvoiceListFilters {
  status?: FiscalInvoiceStatus;
  search?: string;
  from?: string;
  to?: string;
  limit?: number;
}

export interface FiscalReceiptListFilters {
  status?: FiscalReceiptStatus;
  printerId?: string;
  from?: string;
  to?: string;
  limit?: number;
}

export function useFiscalApi() {
  const { get, put, post, delete: del, postFormData, getBlob } = useApi();
  const base = (nip: string) => `/companies/${nip}/fiscal`;

  // Settings
  const getSettings = useCallback(
    async (nip: string): Promise<FiscalSettingsDto> =>
      unwrap(await get<FiscalSettingsDto>(`${base(nip)}/settings`), 'Failed to fetch fiscal settings'),
    [get],
  );

  const updateSettings = useCallback(
    async (nip: string, dto: Partial<FiscalSettingsDto>): Promise<FiscalSettingsDto> =>
      unwrap(await put<FiscalSettingsDto>(`${base(nip)}/settings`, dto), 'Failed to update fiscal settings'),
    [put],
  );

  // Certificates
  const uploadCertificate = useCallback(
    async (nip: string, env: KsefEnvironment, file: File, password?: string): Promise<FiscalCertificateInfo> => {
      const formData = new FormData();
      formData.append('certificate', file);
      if (password) formData.append('password', password);
      return unwrap(
        await postFormData<FiscalCertificateInfo>(`${base(nip)}/certificates/${env}`, formData),
        'Failed to upload certificate',
      );
    },
    [postFormData],
  );

  const deleteCertificate = useCallback(
    async (nip: string, env: KsefEnvironment): Promise<void> =>
      unwrapVoid(await del<void>(`${base(nip)}/certificates/${env}`), 'Failed to delete certificate'),
    [del],
  );

  // Receipt PDF logo
  const uploadLogo = useCallback(
    async (nip: string, file: File): Promise<FiscalSettingsDto> => {
      const formData = new FormData();
      formData.append('logo', file);
      return unwrap(
        await postFormData<FiscalSettingsDto>(`${base(nip)}/logo`, formData),
        'Failed to upload receipt logo',
      );
    },
    [postFormData],
  );

  const deleteLogo = useCallback(
    async (nip: string): Promise<FiscalSettingsDto> =>
      unwrap(await del<FiscalSettingsDto>(`${base(nip)}/logo`), 'Failed to remove receipt logo'),
    [del],
  );

  // Printers
  const listPrinters = useCallback(
    async (nip: string): Promise<FiscalPrinterDto[]> =>
      unwrap(await get<FiscalPrinterDto[]>(`${base(nip)}/printers`), 'Failed to fetch printers'),
    [get],
  );

  const createPrinter = useCallback(
    async (nip: string, dto: CreateFiscalPrinterDto): Promise<{ printer: FiscalPrinterDto; token: string }> =>
      unwrap(
        await post<{ printer: FiscalPrinterDto; token: string }>(`${base(nip)}/printers`, dto),
        'Failed to create printer',
      ),
    [post],
  );

  const updatePrinter = useCallback(
    async (nip: string, id: string, dto: UpdateFiscalPrinterDto): Promise<FiscalPrinterDto> =>
      unwrap(await put<FiscalPrinterDto>(`${base(nip)}/printers/${id}`, dto), 'Failed to update printer'),
    [put],
  );

  const regeneratePrinterToken = useCallback(
    async (nip: string, id: string): Promise<{ token: string }> =>
      unwrap(
        await post<{ token: string }>(`${base(nip)}/printers/${id}/regenerate-token`, {}),
        'Failed to regenerate printer token',
      ),
    [post],
  );

  const deletePrinter = useCallback(
    async (nip: string, id: string): Promise<void> =>
      unwrapVoid(await del<void>(`${base(nip)}/printers/${id}`), 'Failed to delete printer'),
    [del],
  );

  // API tokens
  const listApiTokens = useCallback(
    async (nip: string): Promise<FiscalApiTokenDto[]> =>
      unwrap(await get<FiscalApiTokenDto[]>(`${base(nip)}/api-tokens`), 'Failed to fetch API tokens'),
    [get],
  );

  const createApiToken = useCallback(
    async (
      nip: string,
      name: string,
      numberingPrefixes?: string[],
      environment?: KsefEnvironment | null,
    ): Promise<{ token: string; dto: FiscalApiTokenDto }> =>
      unwrap(
        await post<{ token: string; dto: FiscalApiTokenDto }>(`${base(nip)}/api-tokens`, {
          name,
          numberingPrefixes,
          environment: environment ?? null,
        }),
        'Failed to create API token',
      ),
    [post],
  );

  const updateApiToken = useCallback(
    async (
      nip: string,
      id: string,
      // environment: pominięte = bez zmian, jawny null = wróć do domyślnego firmy
      patch: { name?: string; numberingPrefixes?: string[]; environment?: KsefEnvironment | null },
    ): Promise<FiscalApiTokenDto> =>
      unwrap(
        await put<FiscalApiTokenDto>(`${base(nip)}/api-tokens/${id}`, patch),
        'Failed to update API token',
      ),
    [put],
  );

  const deleteApiToken = useCallback(
    async (nip: string, id: string): Promise<void> =>
      unwrapVoid(await del<void>(`${base(nip)}/api-tokens/${id}`), 'Failed to revoke API token'),
    [del],
  );

  // Invoices (read-only passthrough — same filters/shapes as fiscal/v1)
  const listInvoices = useCallback(
    async (nip: string, filters: FiscalInvoiceListFilters = {}): Promise<FiscalInvoiceListItem[]> => {
      const q = new URLSearchParams();
      if (filters.status) q.set('status', filters.status);
      if (filters.search) q.set('search', filters.search);
      if (filters.from) q.set('from', filters.from);
      if (filters.to) q.set('to', filters.to);
      if (filters.limit) q.set('limit', String(filters.limit));
      const qs = q.toString();
      return unwrap(
        await get<FiscalInvoiceListItem[]>(`${base(nip)}/invoices${qs ? `?${qs}` : ''}`),
        'Failed to fetch fiscal invoices',
      );
    },
    [get],
  );

  const getInvoice = useCallback(
    async (nip: string, uuid: string): Promise<FiscalInvoiceListItem> =>
      unwrap(await get<FiscalInvoiceListItem>(`${base(nip)}/invoices/${uuid}`), 'Failed to fetch fiscal invoice'),
    [get],
  );

  const getInvoiceEvents = useCallback(
    async (nip: string, uuid: string): Promise<FiscalEventItem[]> =>
      unwrap(
        await get<FiscalEventItem[]>(`${base(nip)}/invoices/${uuid}/events`),
        'Failed to fetch invoice events',
      ),
    [get],
  );

  const getInvoiceXml = useCallback(
    async (nip: string, uuid: string): Promise<Blob> => getBlob(`${base(nip)}/invoices/${uuid}/xml`),
    [getBlob],
  );

  const getInvoiceUpo = useCallback(
    async (nip: string, uuid: string): Promise<Blob> => getBlob(`${base(nip)}/invoices/${uuid}/upo`),
    [getBlob],
  );

  const getInvoiceQrPng = useCallback(
    async (nip: string, uuid: string): Promise<Blob> => getBlob(`${base(nip)}/invoices/${uuid}/qr.png`),
    [getBlob],
  );

  const getInvoiceQrSvg = useCallback(
    async (nip: string, uuid: string): Promise<Blob> => getBlob(`${base(nip)}/invoices/${uuid}/qr.svg`),
    [getBlob],
  );

  /** Server returns 409 + {ok:false} for a non-retryable status — that's a
   *  normal outcome, not a thrown error, so the 409 branch is handled here
   *  rather than via unwrap (which would throw a generic "HTTP error: 409"). */
  const retryInvoice = useCallback(
    async (nip: string, uuid: string): Promise<{ ok: boolean }> => {
      const res = await post<{ ok: boolean }>(`${base(nip)}/invoices/${uuid}/retry`, {});
      if (res.error === AUTH_ERROR) throw new SessionExpiredError();
      if (!res.success) {
        if (res.status === 409) return { ok: false };
        throw new Error(res.error || 'Failed to retry invoice');
      }
      return res.data ?? { ok: true };
    },
    [post],
  );

  // Receipts (read-only passthrough — same filters/shapes as fiscal/v1)
  const listReceipts = useCallback(
    async (nip: string, filters: FiscalReceiptListFilters = {}): Promise<FiscalReceiptListItem[]> => {
      const q = new URLSearchParams();
      if (filters.status) q.set('status', filters.status);
      if (filters.printerId) q.set('printerId', filters.printerId);
      if (filters.from) q.set('from', filters.from);
      if (filters.to) q.set('to', filters.to);
      if (filters.limit) q.set('limit', String(filters.limit));
      const qs = q.toString();
      return unwrap(
        await get<FiscalReceiptListItem[]>(`${base(nip)}/receipts${qs ? `?${qs}` : ''}`),
        'Failed to fetch fiscal receipts',
      );
    },
    [get],
  );

  const getReceipt = useCallback(
    async (nip: string, uuid: string): Promise<FiscalReceiptListItem> =>
      unwrap(await get<FiscalReceiptListItem>(`${base(nip)}/receipts/${uuid}`), 'Failed to fetch fiscal receipt'),
    [get],
  );

  const getReceiptEvents = useCallback(
    async (nip: string, uuid: string): Promise<FiscalEventItem[]> =>
      unwrap(
        await get<FiscalEventItem[]>(`${base(nip)}/receipts/${uuid}/events`),
        'Failed to fetch receipt events',
      ),
    [get],
  );

  const getReceiptPdf = useCallback(
    async (nip: string, uuid: string): Promise<Blob> => getBlob(`${base(nip)}/receipts/${uuid}/pdf`),
    [getBlob],
  );

  const retryReceipt = useCallback(
    async (nip: string, uuid: string): Promise<{ ok: boolean }> => {
      const res = await post<{ ok: boolean }>(`${base(nip)}/receipts/${uuid}/retry`, {});
      if (res.error === AUTH_ERROR) throw new SessionExpiredError();
      if (!res.success) {
        if (res.status === 409) return { ok: false };
        throw new Error(res.error || 'Failed to retry receipt');
      }
      return res.data ?? { ok: true };
    },
    [post],
  );

  // Stats
  const getStats = useCallback(
    async (nip: string): Promise<FiscalStatsDto> =>
      unwrap(await get<FiscalStatsDto>(`${base(nip)}/stats`), 'Failed to fetch fiscal stats'),
    [get],
  );

  // Invoice numbering counters (migration-continuity seeding)
  const getNumberingCounters = useCallback(
    async (nip: string): Promise<FiscalNumberingCounterDto[]> =>
      unwrap(
        await get<FiscalNumberingCounterDto[]>(`${base(nip)}/numbering`),
        'Failed to fetch numbering counters',
      ),
    [get],
  );

  const seedNumberingCounter = useCallback(
    async (nip: string, dto: SeedNumberingCounterDto): Promise<FiscalNumberingCounterDto> =>
      unwrap(
        await put<FiscalNumberingCounterDto>(`${base(nip)}/numbering`, dto),
        'Failed to set numbering counter',
      ),
    [put],
  );

  return {
    getSettings, updateSettings,
    uploadCertificate, deleteCertificate,
    uploadLogo, deleteLogo,
    listPrinters, createPrinter, updatePrinter, regeneratePrinterToken, deletePrinter,
    listApiTokens, createApiToken, updateApiToken, deleteApiToken,
    listInvoices, getInvoice, getInvoiceEvents, getInvoiceXml, getInvoiceUpo, getInvoiceQrPng, getInvoiceQrSvg,
    retryInvoice,
    listReceipts, getReceipt, getReceiptEvents, getReceiptPdf, retryReceipt,
    getStats,
    getNumberingCounters, seedNumberingCounter,
  };
}

export function useMileageApi() {
  const { get, post, put, delete: del, getBlob } = useApi();
  const base = (nip: string) => `/companies/${nip}/mileage`;

  // Vehicles
  const getVehicles = useCallback(
    async (nip: string): Promise<VehicleListItem[]> => {
      return unwrap(await get<VehicleListItem[]>(`${base(nip)}/vehicles`), 'Failed to fetch vehicles');
    },
    [get],
  );

  const createVehicle = useCallback(
    async (nip: string, dto: CreateVehicleDto): Promise<VehicleListItem> => {
      return unwrap(await post<VehicleListItem>(`${base(nip)}/vehicles`, dto), 'Failed to create vehicle');
    },
    [post],
  );

  const updateVehicle = useCallback(
    async (nip: string, vehicleId: string, dto: UpdateVehicleDto): Promise<void> => {
      unwrapVoid(await put<void>(`${base(nip)}/vehicles/${vehicleId}`, dto), 'Failed to update vehicle');
    },
    [put],
  );

  const deleteVehicle = useCallback(
    async (nip: string, vehicleId: string): Promise<void> => {
      unwrapVoid(await del<void>(`${base(nip)}/vehicles/${vehicleId}`), 'Failed to delete vehicle');
    },
    [del],
  );

  // Drivers
  const addDriver = useCallback(
    async (nip: string, vehicleId: string, dto: CreateDriverDto): Promise<{ id: string }> => {
      return unwrap(
        await post<{ id: string }>(`${base(nip)}/vehicles/${vehicleId}/drivers`, dto),
        'Failed to add driver',
      );
    },
    [post],
  );

  const deleteDriver = useCallback(
    async (nip: string, vehicleId: string, driverId: string): Promise<void> => {
      unwrapVoid(
        await del<void>(`${base(nip)}/vehicles/${vehicleId}/drivers/${driverId}`),
        'Failed to delete driver',
      );
    },
    [del],
  );

  // Routes
  const getRoutes = useCallback(
    async (nip: string): Promise<StandardRouteListItem[]> => {
      return unwrap(await get<StandardRouteListItem[]>(`${base(nip)}/routes`), 'Failed to fetch routes');
    },
    [get],
  );

  const createRoute = useCallback(
    async (nip: string, dto: CreateStandardRouteDto): Promise<StandardRouteListItem> => {
      return unwrap(await post<StandardRouteListItem>(`${base(nip)}/routes`, dto), 'Failed to create route');
    },
    [post],
  );

  const updateRoute = useCallback(
    async (nip: string, routeId: string, dto: UpdateStandardRouteDto): Promise<void> => {
      unwrapVoid(await put<void>(`${base(nip)}/routes/${routeId}`, dto), 'Failed to update route');
    },
    [put],
  );

  const deleteRoute = useCallback(
    async (nip: string, routeId: string): Promise<void> => {
      unwrapVoid(await del<void>(`${base(nip)}/routes/${routeId}`), 'Failed to delete route');
    },
    [del],
  );

  const calculateDistance = useCallback(
    async (nip: string, dto: CalculateDistanceDto): Promise<CalculateDistanceResult> => {
      const data = unwrap(
        await post<CalculateDistanceResult[] | CalculateDistanceResult>(`${base(nip)}/routes/calculate-distance`, dto),
        'Failed to calculate distance',
      );
      // Backend now returns array, but for backwards compat return first result
      return Array.isArray(data) ? data[0] : data;
    },
    [post],
  );

  const calculateDistanceAll = useCallback(
    async (nip: string, dto: CalculateDistanceDto): Promise<CalculateDistanceResult[]> => {
      return unwrap(
        await post<CalculateDistanceResult[]>(`${base(nip)}/routes/calculate-distance`, dto),
        'Failed to calculate distance',
      );
    },
    [post],
  );

  const searchPlace = useCallback(
    async (nip: string, query: string) => {
      return unwrap(
        await post<{ name: string; formattedAddress: string; address: Record<string, string> }[]>(
          `${base(nip)}/places/search`,
          { query },
        ),
        'Failed to search place',
      );
    },
    [post],
  );

  // Records
  const getRecords = useCallback(
    async (nip: string, vehicleId: string): Promise<MileageRecordSummary[]> => {
      return unwrap(
        await get<MileageRecordSummary[]>(`${base(nip)}/vehicles/${vehicleId}/records`),
        'Failed to fetch records',
      );
    },
    [get],
  );

  const getRecord = useCallback(
    async (nip: string, vehicleId: string, year: number, month: number): Promise<MileageRecordDetail> => {
      return unwrap(
        await get<MileageRecordDetail>(`${base(nip)}/vehicles/${vehicleId}/records/${year}/${month}`),
        'Failed to fetch record',
      );
    },
    [get],
  );

  const updateRecord = useCallback(
    async (nip: string, recordId: string, data: { odometerStart?: number; odometerEnd?: number }): Promise<void> => {
      unwrapVoid(await put<void>(`${base(nip)}/records/${recordId}`, data), 'Failed to update record');
    },
    [put],
  );

  // Entries
  const createEntry = useCallback(
    async (nip: string, recordId: string, dto: CreateMileageEntryDto): Promise<MileageEntryItem> => {
      return unwrap(
        await post<MileageEntryItem>(`${base(nip)}/records/${recordId}/entries`, dto),
        'Failed to create entry',
      );
    },
    [post],
  );

  const updateEntry = useCallback(
    async (nip: string, recordId: string, entryId: string, dto: UpdateMileageEntryDto): Promise<void> => {
      unwrapVoid(
        await put<void>(`${base(nip)}/records/${recordId}/entries/${entryId}`, dto),
        'Failed to update entry',
      );
    },
    [put],
  );

  const deleteEntry = useCallback(
    async (nip: string, recordId: string, entryId: string): Promise<void> => {
      unwrapVoid(
        await del<void>(`${base(nip)}/records/${recordId}/entries/${entryId}`),
        'Failed to delete entry',
      );
    },
    [del],
  );

  const reorderEntries = useCallback(
    async (nip: string, recordId: string, entryIds: string[]): Promise<void> => {
      unwrapVoid(
        await put<void>(`${base(nip)}/records/${recordId}/entries/reorder`, { entryIds }),
        'Failed to reorder entries',
      );
    },
    [put],
  );

  // Trip candidates
  const getTripCandidates = useCallback(
    async (nip: string, vehicleId: string, year: number, month: number): Promise<TripCandidate[]> => {
      return unwrap(
        await get<TripCandidate[]>(`${base(nip)}/vehicles/${vehicleId}/trip-candidates/${year}/${month}`),
        'Failed to fetch trip candidates',
      );
    },
    [get],
  );

  // Reconciliation
  const reconcile = useCallback(
    async (nip: string, recordId: string): Promise<ReconciliationResult> => {
      return unwrap(
        await post<ReconciliationResult>(`${base(nip)}/records/${recordId}/reconcile`, {}),
        'Failed to reconcile',
      );
    },
    [post],
  );

  // PDF (authenticated blob fetch). Saved as a file instead of opened in a
  // tab — window.open is blocked in some hosts (see utils/download.ts).
  const downloadRecordPdf = useCallback(
    async (nip: string, recordId: string, fileName: string): Promise<void> =>
      downloadViaApi(getBlob, `${base(nip)}/records/${recordId}/pdf`, fileName),
    [getBlob],
  );

  // Finalize
  const finalizeRecord = useCallback(
    async (nip: string, recordId: string): Promise<void> => {
      unwrapVoid(
        await post<void>(`${base(nip)}/records/${recordId}/finalize`, {}),
        'Failed to finalize record',
      );
    },
    [post],
  );

  // Places
  const getPlaces = useCallback(
    async (nip: string, vehicleId: string): Promise<MileagePlace[]> => {
      return unwrap(
        await get<MileagePlace[]>(`${base(nip)}/vehicles/${vehicleId}/places`),
        'Failed to fetch places',
      );
    },
    [get],
  );

  const createMileagePlace = useCallback(
    async (nip: string, vehicleId: string, dto: CreateMileagePlaceDto): Promise<MileagePlace> => {
      return unwrap(
        await post<MileagePlace>(`${base(nip)}/vehicles/${vehicleId}/places`, dto),
        'Failed to create place',
      );
    },
    [post],
  );

  const updateMileagePlace = useCallback(
    async (nip: string, vehicleId: string, placeId: string, dto: UpdateMileagePlaceDto): Promise<void> => {
      unwrapVoid(
        await put<void>(`${base(nip)}/vehicles/${vehicleId}/places/${placeId}`, dto),
        'Failed to update place',
      );
    },
    [put],
  );

  const deleteMileagePlace = useCallback(
    async (nip: string, vehicleId: string, placeId: string): Promise<void> => {
      unwrapVoid(
        await del<void>(`${base(nip)}/vehicles/${vehicleId}/places/${placeId}`),
        'Failed to delete place',
      );
    },
    [del],
  );

  const computePlaceRoutes = useCallback(
    async (nip: string, vehicleId: string, placeId: string): Promise<{ alternativesCount: number }> => {
      return unwrap(
        await post<{ alternativesCount: number }>(
          `${base(nip)}/vehicles/${vehicleId}/places/${placeId}/compute-routes`,
          {},
        ),
        'Failed to compute routes',
      );
    },
    [post],
  );

  // AI Generate
  const generateTrips = useCallback(
    async (nip: string, recordId: string, driverName: string): Promise<GenerateTripsResult> => {
      return unwrap(
        await post<GenerateTripsResult>(`${base(nip)}/records/${recordId}/generate-trips`, { driverName }),
        'Failed to generate trips',
      );
    },
    [post],
  );

  return {
    getVehicles, createVehicle, updateVehicle, deleteVehicle,
    addDriver, deleteDriver,
    getRoutes, createRoute, updateRoute, deleteRoute, calculateDistance, calculateDistanceAll, searchPlace,
    getRecords, getRecord, updateRecord,
    createEntry, updateEntry, deleteEntry, reorderEntries,
    getTripCandidates,
    reconcile, downloadRecordPdf, finalizeRecord,
    getPlaces, createMileagePlace, updateMileagePlace, deleteMileagePlace, computePlaceRoutes,
    generateTrips,
  };
}

export function useUploadApi() {
  const { postFormData } = useApi();

  const uploadInvoice = useCallback(
    async (
      file: File,
      options?: { nip?: string; description?: string }
    ): Promise<UploadInvoiceResponse> => {
      const formData = new FormData();
      formData.append('file', file);
      if (options?.nip) formData.append('nip', options.nip);
      if (options?.description) formData.append('description', options.description);

      return unwrap(
        await postFormData<UploadInvoiceResponse>('/invoice/upload', formData),
        'Failed to upload invoice',
      );
    },
    [postFormData]
  );

  return { uploadInvoice };
}

import type { ApiTokenCreated, ApiTokenStatus } from '@ip/shared-types';

export function useApiTokenApi() {
  const { get, post, delete: del } = useApi();

  const getApiToken = useCallback(
    async (): Promise<ApiTokenStatus> =>
      unwrap(await get<ApiTokenStatus>('/auth/api-token'), 'Failed to load API token'),
    [get],
  );

  const generateApiToken = useCallback(
    async (): Promise<ApiTokenCreated> =>
      unwrap(await post<ApiTokenCreated>('/auth/api-token', {}), 'Failed to generate API token'),
    [post],
  );

  const revokeApiToken = useCallback(
    async (): Promise<void> =>
      unwrapVoid(await del<void>('/auth/api-token'), 'Failed to revoke API token'),
    [del],
  );

  return { getApiToken, generateApiToken, revokeApiToken };
}

import type {
  EmployeeListItem,
  EmployeeDetail,
  EmployeeRate,
  EmployeeMonthlyHoursRow,
  EmployeeDocument,
  CostReport,
  CreateEmployeeDto,
  UpdateEmployeeDto,
  CreateRateDto,
  UpdateRateDto,
  UpsertHoursDto,
  EmploymentType,
  EmployeeStatus,
  EmployeePayrollResponse,
  DocumentComplianceType,
} from '@ip/shared-types';

export interface EmployeesListQuery {
  search?: string;
  type?: EmploymentType;
  status?: EmployeeStatus | 'all';
  includeArchived?: boolean;
  page?: number;
  pageSize?: number;
}

export function useEmployeesApi() {
  const { get, post, put, delete: del, postFormData, putFormData, getBlob } = useApi();

  const list = useCallback(
    async (companyNip: string, query: EmployeesListQuery = {}): Promise<PaginatedResponse<EmployeeListItem>> => {
      const params = new URLSearchParams();
      if (query.search) params.set('search', query.search);
      if (query.type) params.set('type', query.type);
      if (query.status) params.set('status', query.status);
      if (query.includeArchived) params.set('includeArchived', 'true');
      if (query.page) params.set('page', String(query.page));
      if (query.pageSize) params.set('pageSize', String(query.pageSize));
      const qs = params.toString();
      return unwrap(
        await get<PaginatedResponse<EmployeeListItem>>(
          `/companies/${companyNip}/employees${qs ? `?${qs}` : ''}`,
        ),
        'Failed to load employees',
      );
    },
    [get],
  );

  const findOne = useCallback(
    async (companyNip: string, id: string): Promise<EmployeeDetail> =>
      unwrap(await get<EmployeeDetail>(`/companies/${companyNip}/employees/${id}`), 'Failed to load employee'),
    [get],
  );

  const create = useCallback(
    async (companyNip: string, dto: CreateEmployeeDto): Promise<EmployeeDetail> =>
      unwrap(await post<EmployeeDetail>(`/companies/${companyNip}/employees`, dto), 'Failed to create employee'),
    [post],
  );

  const update = useCallback(
    async (companyNip: string, id: string, dto: UpdateEmployeeDto): Promise<EmployeeDetail> =>
      unwrap(await put<EmployeeDetail>(`/companies/${companyNip}/employees/${id}`, dto), 'Failed to update employee'),
    [put],
  );

  const remove = useCallback(
    async (companyNip: string, id: string): Promise<void> =>
      unwrapVoid(await del<void>(`/companies/${companyNip}/employees/${id}`), 'Failed to delete employee'),
    [del],
  );

  const terminate = useCallback(
    async (companyNip: string, id: string, terminatedAt?: string): Promise<void> =>
      unwrapVoid(
        await post<void>(`/companies/${companyNip}/employees/${id}/terminate`, { terminatedAt }),
        'Failed to terminate employee',
      ),
    [post],
  );

  const archive = useCallback(
    async (companyNip: string, id: string): Promise<void> =>
      unwrapVoid(await post<void>(`/companies/${companyNip}/employees/${id}/archive`, {}), 'Failed to archive employee'),
    [post],
  );

  const unarchive = useCallback(
    async (companyNip: string, id: string): Promise<void> =>
      unwrapVoid(await post<void>(`/companies/${companyNip}/employees/${id}/unarchive`, {}), 'Failed to unarchive employee'),
    [post],
  );

  // Rates
  const listRates = useCallback(
    async (companyNip: string, employeeId: string): Promise<EmployeeRate[]> =>
      unwrap(await get<EmployeeRate[]>(`/companies/${companyNip}/employees/${employeeId}/rates`), 'Failed to load rates'),
    [get],
  );

  const addRate = useCallback(
    async (companyNip: string, employeeId: string, dto: CreateRateDto): Promise<void> =>
      unwrapVoid(await post<void>(`/companies/${companyNip}/employees/${employeeId}/rates`, dto), 'Failed to add rate'),
    [post],
  );

  const updateRate = useCallback(
    async (companyNip: string, employeeId: string, rateId: string, dto: UpdateRateDto): Promise<void> =>
      unwrapVoid(
        await put<void>(`/companies/${companyNip}/employees/${employeeId}/rates/${rateId}`, dto),
        'Failed to update rate',
      ),
    [put],
  );

  const removeRate = useCallback(
    async (companyNip: string, employeeId: string, rateId: string): Promise<void> =>
      unwrapVoid(await del<void>(`/companies/${companyNip}/employees/${employeeId}/rates/${rateId}`), 'Failed to delete rate'),
    [del],
  );

  // Hours
  const listHours = useCallback(
    async (companyNip: string, employeeId: string, year: number): Promise<EmployeeMonthlyHoursRow[]> =>
      unwrap(
        await get<EmployeeMonthlyHoursRow[]>(`/companies/${companyNip}/employees/${employeeId}/hours?year=${year}`),
        'Failed to load hours',
      ),
    [get],
  );

  const upsertHours = useCallback(
    async (companyNip: string, employeeId: string, dto: UpsertHoursDto): Promise<void> =>
      unwrapVoid(await put<void>(`/companies/${companyNip}/employees/${employeeId}/hours`, dto), 'Failed to save hours'),
    [put],
  );

  // Documents
  const listDocuments = useCallback(
    async (companyNip: string, employeeId: string): Promise<EmployeeDocument[]> =>
      unwrap(
        await get<EmployeeDocument[]>(`/companies/${companyNip}/employees/${employeeId}/documents`),
        'Failed to load documents',
      ),
    [get],
  );

  const addDocument = useCallback(
    async (
      companyNip: string,
      employeeId: string,
      file: File,
      label: string,
      expiryDate?: string,
      complianceType?: DocumentComplianceType,
    ): Promise<EmployeeDocument> => {
      const fd = new FormData();
      fd.append('file', file);
      if (label) fd.append('label', label);
      if (expiryDate) fd.append('expiryDate', expiryDate);
      if (complianceType) fd.append('complianceType', complianceType);
      return unwrap(
        await postFormData<EmployeeDocument>(`/companies/${companyNip}/employees/${employeeId}/documents`, fd),
        'Failed to upload document',
      );
    },
    [postFormData],
  );

  const downloadDocument = useCallback(
    async (companyNip: string, employeeId: string, docId: string, fileName: string): Promise<void> =>
      downloadViaApi(
        getBlob,
        `/companies/${companyNip}/employees/${employeeId}/documents/${docId}/file`,
        fileName,
      ),
    [getBlob],
  );

  const removeDocument = useCallback(
    async (companyNip: string, employeeId: string, docId: string): Promise<void> =>
      unwrapVoid(
        await del<void>(`/companies/${companyNip}/employees/${employeeId}/documents/${docId}`),
        'Failed to delete document',
      ),
    [del],
  );

  // Student card
  const setStudentCard = useCallback(
    async (companyNip: string, employeeId: string, file: File, expiryDate: string): Promise<void> => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('expiryDate', expiryDate);
      return unwrapVoid(
        await putFormData<void>(`/companies/${companyNip}/employees/${employeeId}/student-card`, fd),
        'Failed to upload student card',
      );
    },
    [putFormData],
  );

  const deleteStudentCard = useCallback(
    async (companyNip: string, employeeId: string): Promise<void> =>
      unwrapVoid(
        await del<void>(`/companies/${companyNip}/employees/${employeeId}/student-card`),
        'Failed to delete student card',
      ),
    [del],
  );

  // No stored filename for the card — downloadViaApi appends an extension
  // inferred from the response MIME type (PDF or photo).
  const downloadStudentCard = useCallback(
    async (companyNip: string, employeeId: string): Promise<void> =>
      downloadViaApi(
        getBlob,
        `/companies/${companyNip}/employees/${employeeId}/student-card/file`,
        'legitymacja-studencka',
      ),
    [getBlob],
  );

  // Cost report
  const costReport = useCallback(
    async (companyNip: string, year: number, month: number): Promise<CostReport> =>
      unwrap(
        await get<CostReport>(`/companies/${companyNip}/employees/cost-report?year=${year}&month=${month}`),
        'Failed to load cost report',
      ),
    [get],
  );

  // Payroll breakdown from the employee's current rate + employment type
  const employeePayroll = useCallback(
    async (companyNip: string, employeeId: string, year?: number, month?: number): Promise<EmployeePayrollResponse> => {
      const q = new URLSearchParams();
      if (year) q.set('year', String(year));
      if (month) q.set('month', String(month));
      const qs = q.toString();
      return unwrap(
        await get<EmployeePayrollResponse>(
          `/companies/${companyNip}/employees/${employeeId}/payroll${qs ? `?${qs}` : ''}`,
        ),
        'Failed to load payroll breakdown',
      );
    },
    [get],
  );

  return {
    list, findOne, create, update, remove, terminate, archive, unarchive,
    listRates, addRate, updateRate, removeRate,
    listHours, upsertHours,
    listDocuments, addDocument, downloadDocument, removeDocument,
    setStudentCard, deleteStudentCard, downloadStudentCard,
    costReport, employeePayroll,
  };
}

// ==================== HR (Tasks HR2–HR4) ====================

import type {
  AbsenceCalendarRow,
  AbsenceCalendarScope,
  AbsenceFilterParams,
  AbsenceListItem,
  ComplianceItem,
  ComplianceListParams,
  CreateSickLeaveDto,
  CreateSickLeaveResponse,
  HolidayItem,
  HrMeResponse,
  LeaveBalanceResponse,
  LeaveRegisterRow,
  SickLeaveImportCommitResponse,
  SickLeaveImportPreviewResponse,
  SickLeaveListItem,
  SubmitAbsenceDto,
  SubmitAbsenceResponse,
  UpdateLeaveConfigDto,
  PayrollBreakdown,
  PayrollCalcRequestDto,
  MyInvoiceItem,
  EarlyPaymentRequestDto,
  NotificationsResponse,
  MarkNotificationsReadDto,
  ReimbursementCandidate,
  ReimbursementItem,
  ReimbursementSearchDto,
  MyDataResponse,
  MyDataRevealResponse,
  HrDocumentItem,
  HrDocumentsResponse,
} from '@ip/shared-types';

export function useHrApi() {
  const { get, post, put, postFormData, getBlob } = useApi();

  const hrMe = useCallback(
    async (nip: string): Promise<HrMeResponse> =>
      unwrap(await get<HrMeResponse>(`/companies/${nip}/hr/me`), 'Failed to load HR context'),
    [get],
  );

  const holidays = useCallback(
    async (nip: string, from: string, to: string): Promise<HolidayItem[]> =>
      unwrap(
        await get<HolidayItem[]>(`/companies/${nip}/hr/holidays?from=${from}&to=${to}`),
        'Failed to load holidays',
      ),
    [get],
  );

  const submitAbsence = useCallback(
    async (nip: string, dto: SubmitAbsenceDto): Promise<SubmitAbsenceResponse> =>
      unwrap(await post<SubmitAbsenceResponse>(`/companies/${nip}/absences`, dto), 'Failed to submit request'),
    [post],
  );

  const listAbsences = useCallback(
    async (nip: string, params: AbsenceFilterParams = {}): Promise<AbsenceListItem[]> => {
      const q = new URLSearchParams();
      if (params.employeeId) q.set('employeeId', params.employeeId);
      if (params.from) q.set('from', params.from);
      if (params.to) q.set('to', params.to);
      if (params.status) q.set('status', params.status);
      if (params.type) q.set('type', params.type);
      const qs = q.toString();
      return unwrap(
        await get<AbsenceListItem[]>(`/companies/${nip}/absences${qs ? `?${qs}` : ''}`),
        'Failed to load absences',
      );
    },
    [get],
  );

  const absenceCalendar = useCallback(
    async (nip: string, from: string, to: string, scope: AbsenceCalendarScope): Promise<AbsenceCalendarRow[]> =>
      unwrap(
        await get<AbsenceCalendarRow[]>(`/companies/${nip}/absences/calendar?from=${from}&to=${to}&scope=${scope}`),
        'Failed to load calendar',
      ),
    [get],
  );

  const approveAbsence = useCallback(
    async (nip: string, id: string, note?: string): Promise<AbsenceListItem> =>
      unwrap(await post<AbsenceListItem>(`/companies/${nip}/absences/${id}/approve`, { note }), 'Failed to approve'),
    [post],
  );

  const rejectAbsence = useCallback(
    async (nip: string, id: string, note?: string): Promise<AbsenceListItem> =>
      unwrap(await post<AbsenceListItem>(`/companies/${nip}/absences/${id}/reject`, { note }), 'Failed to reject'),
    [post],
  );

  const cancelAbsence = useCallback(
    async (nip: string, id: string, note?: string): Promise<AbsenceListItem> =>
      unwrap(await post<AbsenceListItem>(`/companies/${nip}/absences/${id}/cancel`, { note }), 'Failed to cancel'),
    [post],
  );

  const leaveBalance = useCallback(
    async (nip: string, employeeId: string, year?: number): Promise<LeaveBalanceResponse> =>
      unwrap(
        await get<LeaveBalanceResponse>(
          `/companies/${nip}/employees/${employeeId}/leave-balance${year ? `?year=${year}` : ''}`,
        ),
        'Failed to load leave balance',
      ),
    [get],
  );

  const leaveRegister = useCallback(
    async (nip: string, year: number): Promise<LeaveRegisterRow[]> =>
      unwrap(
        await get<LeaveRegisterRow[]>(`/companies/${nip}/hr/entitlements?year=${year}`),
        'Failed to load leave register',
      ),
    [get],
  );

  const updateLeaveConfig = useCallback(
    async (nip: string, employeeId: string, dto: UpdateLeaveConfigDto): Promise<LeaveRegisterRow> =>
      unwrap(
        await put<LeaveRegisterRow>(`/companies/${nip}/hr/entitlements/${employeeId}`, dto),
        'Failed to update leave configuration',
      ),
    [put],
  );

  const sickLeaves = useCallback(
    async (nip: string, from?: string, to?: string): Promise<SickLeaveListItem[]> => {
      const q = new URLSearchParams();
      if (from) q.set('from', from);
      if (to) q.set('to', to);
      const qs = q.toString();
      return unwrap(
        await get<SickLeaveListItem[]>(`/companies/${nip}/sick-leaves${qs ? `?${qs}` : ''}`),
        'Failed to load sick leaves',
      );
    },
    [get],
  );

  const createSickLeave = useCallback(
    async (nip: string, dto: CreateSickLeaveDto, override = false): Promise<CreateSickLeaveResponse> =>
      unwrap(
        await post<CreateSickLeaveResponse>(
          `/companies/${nip}/sick-leaves${override ? '?override=true' : ''}`,
          dto,
        ),
        'Failed to record sick leave',
      ),
    [post],
  );

  const previewSickLeaveImport = useCallback(
    async (nip: string, file: File): Promise<SickLeaveImportPreviewResponse> => {
      const fd = new FormData();
      fd.append('file', file);
      return unwrap(
        await postFormData<SickLeaveImportPreviewResponse>(`/companies/${nip}/sick-leaves/import`, fd),
        'Failed to preview import',
      );
    },
    [postFormData],
  );

  const commitSickLeaveImport = useCallback(
    async (nip: string, file: File, rowIndices: number[]): Promise<SickLeaveImportCommitResponse> => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('rows', JSON.stringify(rowIndices));
      return unwrap(
        await postFormData<SickLeaveImportCommitResponse>(
          `/companies/${nip}/sick-leaves/import?dryRun=false`,
          fd,
        ),
        'Failed to commit import',
      );
    },
    [postFormData],
  );

  const payrollCalc = useCallback(
    async (nip: string, body: PayrollCalcRequestDto): Promise<PayrollBreakdown> =>
      unwrap(
        await post<PayrollBreakdown>(`/companies/${nip}/hr/payroll/calc`, body),
        'Failed to calculate payroll',
      ),
    [post],
  );

  // ===== Compliance (deadlines & reminders) =====

  const listCompliance = useCallback(
    async (nip: string, params: ComplianceListParams = {}): Promise<ComplianceItem[]> => {
      const q = new URLSearchParams();
      if (params.status) q.set('status', params.status);
      if (params.type) q.set('type', params.type);
      const qs = q.toString();
      return unwrap(
        await get<ComplianceItem[]>(`/companies/${nip}/hr/compliance${qs ? `?${qs}` : ''}`),
        'Failed to load compliance',
      );
    },
    [get],
  );

  const submitCompliance = useCallback(
    async (nip: string, id: string, file: File, validUntil: string): Promise<ComplianceItem> => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('validUntil', validUntil);
      return unwrap(
        await postFormData<ComplianceItem>(`/companies/${nip}/hr/compliance/${id}/submit`, fd),
        'Failed to submit document',
      );
    },
    [postFormData],
  );

  const verifyCompliance = useCallback(
    async (nip: string, id: string): Promise<ComplianceItem> =>
      unwrap(
        await post<ComplianceItem>(`/companies/${nip}/hr/compliance/${id}/verify`, {}),
        'Failed to verify document',
      ),
    [post],
  );

  const rejectCompliance = useCallback(
    async (nip: string, id: string, reason: string): Promise<ComplianceItem> =>
      unwrap(
        await post<ComplianceItem>(`/companies/${nip}/hr/compliance/${id}/reject`, { reason }),
        'Failed to reject document',
      ),
    [post],
  );

  const complianceDocumentDownload = useCallback(
    async (nip: string, id: string, fileName: string): Promise<void> =>
      downloadViaApi(getBlob, `/companies/${nip}/hr/compliance/${id}/document-file`, fileName),
    [getBlob],
  );

  const myInvoices = useCallback(
    async (nip: string): Promise<MyInvoiceItem[]> =>
      unwrap(await get<MyInvoiceItem[]>(`/companies/${nip}/hr/my-invoices`), 'Failed to load invoices'),
    [get],
  );

  const requestEarlyPayment = useCallback(
    async (nip: string, invoiceId: string, dto: EarlyPaymentRequestDto): Promise<void> =>
      unwrapVoid(
        await post<void>(`/companies/${nip}/hr/my-invoices/${invoiceId}/early-payment`, dto),
        'Failed to request early payment',
      ),
    [post],
  );

  const fundsAlarm = useCallback(
    async (nip: string, invoiceId: string): Promise<void> =>
      unwrapVoid(
        await post<void>(`/companies/${nip}/hr/my-invoices/${invoiceId}/funds-alarm`, {}),
        'Failed to raise the alarm',
      ),
    [post],
  );

  const payAlarm = useCallback(
    async (nip: string): Promise<void> =>
      unwrapVoid(await post<void>(`/companies/${nip}/hr/pay-alarm`, {}), 'Failed to raise the alarm'),
    [post],
  );

  // Employee expense reimbursements (zwroty)
  const searchReimbursements = useCallback(
    async (nip: string, dto: ReimbursementSearchDto): Promise<ReimbursementCandidate[]> =>
      unwrap(
        await post<ReimbursementCandidate[]>(`/companies/${nip}/hr/reimbursements/search`, dto),
        'Failed to search invoices',
      ),
    [post],
  );

  const createReimbursement = useCallback(
    async (nip: string, invoiceId: string): Promise<ReimbursementItem> =>
      unwrap(
        await post<ReimbursementItem>(`/companies/${nip}/hr/reimbursements`, { invoiceId }),
        'Failed to request the reimbursement',
      ),
    [post],
  );

  const myReimbursements = useCallback(
    async (nip: string): Promise<ReimbursementItem[]> =>
      unwrap(await get<ReimbursementItem[]>(`/companies/${nip}/hr/reimbursements/mine`), 'Failed to load reimbursements'),
    [get],
  );

  const reimbursementApprovals = useCallback(
    async (nip: string): Promise<ReimbursementItem[]> =>
      unwrap(
        await get<ReimbursementItem[]>(`/companies/${nip}/hr/reimbursements/approvals`),
        'Failed to load approvals',
      ),
    [get],
  );

  const approveReimbursement = useCallback(
    async (nip: string, id: string): Promise<void> =>
      unwrapVoid(await post<void>(`/companies/${nip}/hr/reimbursements/${id}/approve`, {}), 'Failed to approve'),
    [post],
  );

  const rejectReimbursement = useCallback(
    async (nip: string, id: string, reason: string): Promise<void> =>
      unwrapVoid(await post<void>(`/companies/${nip}/hr/reimbursements/${id}/reject`, { reason }), 'Failed to reject'),
    [post],
  );

  // HR "Moje dane" (read-only self view; sensitive values PESEL-gated)
  const myData = useCallback(
    async (nip: string): Promise<MyDataResponse> =>
      unwrap(await get<MyDataResponse>(`/companies/${nip}/hr/my-data`), 'Failed to load personal data'),
    [get],
  );

  const revealMyData = useCallback(
    async (nip: string, peselLast4: string): Promise<MyDataRevealResponse> =>
      unwrap(
        await post<MyDataRevealResponse>(`/companies/${nip}/hr/my-data/reveal`, { peselLast4 }),
        'Failed to reveal data',
      ),
    [post],
  );

  const unlockMyData = useCallback(
    async (nip: string, employeeId: string): Promise<void> =>
      unwrapVoid(
        await post<void>(`/companies/${nip}/hr/my-data/${employeeId}/unlock`, {}),
        'Failed to unlock',
      ),
    [post],
  );

  // HR "Dokumenty" (Task 7): pending/done acceptances + document library,
  // download and electronic (PESEL-last-4) acceptance.
  const hrDocuments = useCallback(
    async (nip: string): Promise<HrDocumentsResponse> =>
      unwrap(await get<HrDocumentsResponse>(`/companies/${nip}/hr/documents`), 'Failed to load documents'),
    [get],
  );

  const hrDocumentDownload = useCallback(
    async (nip: string, publicationId: string, fileName: string): Promise<void> =>
      downloadViaApi(getBlob, `/companies/${nip}/hr/documents/${publicationId}/file`, fileName),
    [getBlob],
  );

  const hrAcceptDocument = useCallback(
    async (nip: string, acceptanceId: string, peselLast4: string): Promise<HrDocumentItem> =>
      unwrap(
        await post<HrDocumentItem>(`/companies/${nip}/hr/documents/acceptances/${acceptanceId}/accept`, { peselLast4 }),
        'Failed to accept the document',
      ),
    [post],
  );

  // Upload-back of a physically-signed copy (documents plan 4): the
  // employee's alternative to mailing the scan to periscope@ — routes by
  // acceptance id, no PSC-D token needed.
  const uploadSignedDocument = useCallback(
    async (nip: string, acceptanceId: string, formData: FormData): Promise<HrDocumentItem> =>
      unwrap(
        await postFormData<HrDocumentItem>(
          `/companies/${nip}/hr/documents/acceptances/${acceptanceId}/upload-signed`,
          formData,
        ),
        'Failed to upload the signed document',
      ),
    [postFormData],
  );

  return {
    hrMe, holidays,
    submitAbsence, listAbsences, absenceCalendar,
    approveAbsence, rejectAbsence, cancelAbsence,
    leaveBalance, leaveRegister, updateLeaveConfig,
    sickLeaves, createSickLeave, previewSickLeaveImport, commitSickLeaveImport,
    payrollCalc,
    listCompliance, submitCompliance, verifyCompliance, rejectCompliance, complianceDocumentDownload,
    myInvoices, requestEarlyPayment, fundsAlarm, payAlarm,
    searchReimbursements, createReimbursement, myReimbursements,
    reimbursementApprovals, approveReimbursement, rejectReimbursement,
    myData, revealMyData, unlockMyData,
    hrDocuments, hrDocumentDownload, hrAcceptDocument, uploadSignedDocument,
  };
}

// ===== Notifications (navbar bell) =====

export function useNotificationsApi() {
  const { get, post } = useApi();

  const listNotifications = useCallback(
    async (nip: string, page = 1, pageSize = 20): Promise<NotificationsResponse> =>
      unwrap(
        await get<NotificationsResponse>(`/companies/${nip}/notifications?page=${page}&pageSize=${pageSize}`),
        'Failed to load notifications',
      ),
    [get],
  );

  const markNotificationsRead = useCallback(
    async (nip: string, dto: MarkNotificationsReadDto): Promise<void> =>
      unwrapVoid(await post<void>(`/companies/${nip}/notifications/read`, dto), 'Failed to mark as read'),
    [post],
  );

  return { listNotifications, markNotificationsRead };
}

// ===== Documents (per-company markdown template repository, spec 2026-07-16) =====

import type {
  DocumentTemplateListItem,
  DocumentTemplateDetail,
  DocumentTemplateVersionInfo,
  DocumentTemplateCategory,
  CreateDocumentTemplateDto,
  UpdateDocumentTemplateDto,
  DocumentPublicationListItem,
  DocumentPublicationDetail,
  DocumentAcceptanceRow,
  SignedRevisionPreview,
  DocumentCountersignRow,
  CreatePublicationDto,
  CreatePublicationResult,
  ContractorDocType,
} from '@ip/shared-types';

/** Body of POST .../documents/templates/:id/generate-contractor (documents plan 4 task 2). */
export interface GenerateContractorDocumentDto {
  contractorId: string;
  docType: ContractorDocType;
  /** Optional — without it the generated row has no expiry date and the reminder cron ignores it. */
  expiryDate?: string;
  label?: string;
}

export function useDocumentsApi() {
  const { get, post, put, postRaw, postFormData, getBlob } = useApi();
  const base = (nip: string) => `/companies/${nip}/documents/templates`;
  const pubBase = (nip: string) => `/companies/${nip}/documents/publications`;

  const listTemplates = useCallback(
    async (
      nip: string,
      params?: { category?: DocumentTemplateCategory; includeArchived?: boolean },
    ): Promise<DocumentTemplateListItem[]> => {
      const query = new URLSearchParams();
      if (params?.category) query.set('category', params.category);
      if (params?.includeArchived) query.set('includeArchived', 'true');
      const qs = query.toString();
      return unwrap(
        await get<DocumentTemplateListItem[]>(`${base(nip)}${qs ? `?${qs}` : ''}`),
        'Failed to fetch templates',
      );
    },
    [get],
  );

  const createTemplate = useCallback(
    async (nip: string, dto: CreateDocumentTemplateDto): Promise<DocumentTemplateDetail> => {
      return unwrap(await post<DocumentTemplateDetail>(base(nip), dto), 'Failed to create template');
    },
    [post],
  );

  const getTemplate = useCallback(
    async (nip: string, id: string): Promise<DocumentTemplateDetail> => {
      return unwrap(await get<DocumentTemplateDetail>(`${base(nip)}/${id}`), 'Failed to fetch template');
    },
    [get],
  );

  const updateTemplate = useCallback(
    async (nip: string, id: string, dto: UpdateDocumentTemplateDto): Promise<DocumentTemplateDetail> => {
      return unwrap(await put<DocumentTemplateDetail>(`${base(nip)}/${id}`, dto), 'Failed to update template');
    },
    [put],
  );

  const archiveTemplate = useCallback(
    async (nip: string, id: string, archived: boolean): Promise<void> => {
      unwrapVoid(
        await post<void>(`${base(nip)}/${id}/archive`, { archived }),
        'Failed to archive template',
      );
    },
    [post],
  );

  const listVersions = useCallback(
    async (nip: string, id: string): Promise<DocumentTemplateVersionInfo[]> => {
      return unwrap(
        await get<DocumentTemplateVersionInfo[]>(`${base(nip)}/${id}/versions`),
        'Failed to fetch versions',
      );
    },
    [get],
  );

  const publishVersion = useCallback(
    async (nip: string, id: string): Promise<{ version: number }> => {
      return unwrap(
        await post<{ version: number }>(`${base(nip)}/${id}/publish-version`, {}),
        'Failed to publish version',
      );
    },
    [post],
  );

  // Starter template (umowa spec 2026-07-23 §7): built-in "Umowa zlecenie
  // (student)" — created AND published (v1) in one call; 409 when an active
  // template with that name already exists.
  const createStarterUmowaZlecenieStudent = useCallback(
    async (nip: string): Promise<DocumentTemplateDetail> => {
      return unwrap(
        await post<DocumentTemplateDetail>(`${base(nip)}/starters/umowa-zlecenie-student`, {}),
        'Failed to create the starter template',
      );
    },
    [post],
  );

  /**
   * Server-rendered PDF preview. postRaw already builds auth headers/base URL
   * like the JSON helpers and throws on a non-2xx response, so it doubles as
   * the "raw fetch" this endpoint needs (the JSON `unwrap` helper doesn't fit
   * a blob body).
   */
  const previewPdf = useCallback(
    async (
      nip: string,
      id: string,
      body: { employeeId?: string; contractorId?: string },
    ): Promise<Blob> => {
      return postRaw(`${base(nip)}/${id}/preview`, body);
    },
    [postRaw],
  );

  // ---- Publications (Task 6, spec §3.5/§6): publish a template version to
  // an employee audience or individual, track acceptance progress. ----

  const listPublications = useCallback(
    async (nip: string, params?: { includeArchived?: boolean }): Promise<DocumentPublicationListItem[]> => {
      const query = new URLSearchParams();
      if (params?.includeArchived) query.set('includeArchived', 'true');
      const qs = query.toString();
      return unwrap(
        await get<DocumentPublicationListItem[]>(`${pubBase(nip)}${qs ? `?${qs}` : ''}`),
        'Failed to fetch publications',
      );
    },
    [get],
  );

  const createPublication = useCallback(
    async (nip: string, dto: CreatePublicationDto): Promise<CreatePublicationResult> => {
      return unwrap(
        await post<CreatePublicationResult>(pubBase(nip), dto),
        'Failed to create publication',
      );
    },
    [post],
  );

  const getPublication = useCallback(
    async (nip: string, id: string): Promise<DocumentPublicationDetail> => {
      return unwrap(
        await get<DocumentPublicationDetail>(`${pubBase(nip)}/${id}`),
        'Failed to fetch publication',
      );
    },
    [get],
  );

  const downloadPublication = useCallback(
    async (nip: string, id: string, fileName: string): Promise<void> =>
      downloadViaApi(getBlob, `${pubBase(nip)}/${id}/file`, fileName),
    [getBlob],
  );

  const archivePublication = useCallback(
    async (nip: string, id: string): Promise<void> => {
      unwrapVoid(await post<void>(`${pubBase(nip)}/${id}/archive`, {}), 'Failed to archive publication');
    },
    [post],
  );

  const markSigned = useCallback(
    async (nip: string, publicationId: string, acceptanceId: string, formData: FormData): Promise<DocumentAcceptanceRow> => {
      return unwrap(
        await postFormData<DocumentAcceptanceRow>(
          `${pubBase(nip)}/${publicationId}/acceptances/${acceptanceId}/mark-signed`,
          formData,
        ),
        'Failed to save the signed scan',
      );
    },
    [postFormData],
  );

  // A NEWER signed revision of an already completed acceptance (e.g. the PDF
  // countersigned by a representative after the employee's signature settled
  // the row). Server-side gates: same-file byte lineage + expected signers only
  // — 409 duplicate/concurrent, 422 refused with the reason in the message.
  const attachSignedRevision = useCallback(
    async (nip: string, publicationId: string, acceptanceId: string, formData: FormData): Promise<DocumentAcceptanceRow> => {
      return unwrap(
        await postFormData<DocumentAcceptanceRow>(
          `${pubBase(nip)}/${publicationId}/acceptances/${acceptanceId}/attach-signed`,
          formData,
        ),
        'Failed to attach the signed revision',
      );
    },
    [postFormData],
  );

  // Dry run of attachSignedRevision: same gates and the same 409/422, nothing
  // written — returns the signatures the file would ADD so the operator sees
  // WHO they are vouching for before the pointer moves.
  const previewSignedRevision = useCallback(
    async (nip: string, publicationId: string, acceptanceId: string, formData: FormData): Promise<SignedRevisionPreview> => {
      return unwrap(
        await postFormData<SignedRevisionPreview>(
          `${pubBase(nip)}/${publicationId}/acceptances/${acceptanceId}/attach-signed?dryRun=1`,
          formData,
        ),
        'Failed to check the signed revision',
      );
    },
    [postFormData],
  );

  // Digital-signature verification of the returned scan (PAdES: podpis
  // kwalifikowany / podpis zaufany) — verifies, persists and returns the result.
  const verifyAcceptanceSignature = useCallback(
    async (nip: string, publicationId: string, acceptanceId: string): Promise<SignatureVerification> =>
      unwrap(
        await post<SignatureVerification>(
          `${pubBase(nip)}/${publicationId}/acceptances/${acceptanceId}/verify-signature`,
          {},
        ),
        'Failed to verify the signature',
      ),
    [post],
  );

  // The scan routed back for an acceptance — HR must be able to open what
  // they are about to confirm. Streamed through our API and saved.
  const downloadAcceptanceReturned = useCallback(
    async (nip: string, publicationId: string, acceptanceId: string, fileName: string): Promise<void> =>
      downloadViaApi(
        getBlob,
        `${pubBase(nip)}/${publicationId}/acceptances/${acceptanceId}/returned-file`,
        fileName,
      ),
    [getBlob],
  );

  // HR confirmation of a returned signed copy (documents plan 4): `returned` -> `signed`.
  const confirmReturn = useCallback(
    async (nip: string, publicationId: string, acceptanceId: string): Promise<DocumentAcceptanceRow> => {
      return unwrap(
        await post<DocumentAcceptanceRow>(
          `${pubBase(nip)}/${publicationId}/acceptances/${acceptanceId}/confirm-return`,
          {},
        ),
        'Failed to confirm the returned document',
      );
    },
    [post],
  );

  // ---- Representative countersigns (umowa spec 2026-07-23 §6): after the
  // employee signs, the selected representatives sign sequentially. ----

  // Records a representative's signature; the file is OPTIONAL (a digitally
  // signed PAdES PDF or a scan — omitted when signed in person on paper).
  const countersignSign = useCallback(
    async (
      nip: string,
      publicationId: string,
      csId: string,
      file?: File | null,
    ): Promise<DocumentCountersignRow> => {
      const fd = new FormData();
      if (file) fd.append('file', file);
      return unwrap(
        await postFormData<DocumentCountersignRow>(
          `${pubBase(nip)}/${publicationId}/countersigns/${csId}/sign`,
          fd,
        ),
        'Failed to record the signature',
      );
    },
    [postFormData],
  );

  // Digital-signature verification of the uploaded countersign file —
  // verifies, persists and returns the result (400 when no file was uploaded).
  const countersignVerifySignature = useCallback(
    async (nip: string, publicationId: string, csId: string): Promise<SignatureVerification> =>
      unwrap(
        await post<SignatureVerification>(
          `${pubBase(nip)}/${publicationId}/countersigns/${csId}/verify-signature`,
          {},
        ),
        'Failed to verify the signature',
      ),
    [post],
  );

  // The uploaded countersign file, streamed through our API and saved.
  const countersignFileDownload = useCallback(
    async (nip: string, publicationId: string, csId: string, fileName: string): Promise<void> =>
      downloadViaApi(
        getBlob,
        `${pubBase(nip)}/${publicationId}/countersigns/${csId}/file-content`,
        fileName,
      ),
    [getBlob],
  );

  // Contractor generate-from-template (documents plan 4 task 2): renders the
  // template's published version straight into the contractor archive.
  const generateContractorDocument = useCallback(
    async (nip: string, templateId: string, dto: GenerateContractorDocumentDto): Promise<ContractorDocument> => {
      return unwrap(
        await post<ContractorDocument>(`${base(nip)}/${templateId}/generate-contractor`, dto),
        'Failed to generate the document',
      );
    },
    [post],
  );

  return {
    listTemplates,
    createTemplate,
    createStarterUmowaZlecenieStudent,
    getTemplate,
    updateTemplate,
    archiveTemplate,
    listVersions,
    publishVersion,
    previewPdf,
    listPublications,
    createPublication,
    getPublication,
    downloadPublication,
    archivePublication,
    markSigned,
    attachSignedRevision,
    previewSignedRevision,
    downloadAcceptanceReturned,
    verifyAcceptanceSignature,
    confirmReturn,
    countersignSign,
    countersignVerifySignature,
    countersignFileDownload,
    generateContractorDocument,
  };
}

export function useWarehouseApi() {
  const { get, put, post, delete: del, postFormData, getBlob } = useApi();
  const base = (nip: string) => `/companies/${nip}/warehouse`;

  // Items (katalog Pozycji)
  const listItems = useCallback(
    async (nip: string, search?: string): Promise<WarehouseItem[]> => {
      const q = new URLSearchParams();
      if (search) q.set('search', search);
      const qs = q.toString();
      return unwrap(
        await get<WarehouseItem[]>(`${base(nip)}/items${qs ? `?${qs}` : ''}`),
        'Failed to fetch warehouse items',
      );
    },
    [get],
  );

  const createItem = useCallback(
    async (nip: string, dto: CreateWarehouseItemDto): Promise<WarehouseItem> =>
      unwrap(await post<WarehouseItem>(`${base(nip)}/items`, dto), 'Failed to create warehouse item'),
    [post],
  );

  const updateItem = useCallback(
    async (nip: string, id: string, dto: UpdateWarehouseItemDto): Promise<WarehouseItem> =>
      unwrap(await put<WarehouseItem>(`${base(nip)}/items/${id}`, dto), 'Failed to update warehouse item'),
    [put],
  );

  const deleteItem = useCallback(
    async (nip: string, id: string): Promise<void> =>
      unwrapVoid(await del<void>(`${base(nip)}/items/${id}`), 'Failed to delete warehouse item'),
    [del],
  );

  // WZ
  const listWz = useCallback(
    async (nip: string, params: WzListParams): Promise<PaginatedResponse<WzListItem>> => {
      const q = new URLSearchParams();
      if (params.from) q.set('from', params.from);
      if (params.to) q.set('to', params.to);
      if (params.contractorId) q.set('contractorId', params.contractorId);
      if (params.status) q.set('status', params.status);
      if (params.search) q.set('search', params.search);
      if (params.page) q.set('page', String(params.page));
      if (params.pageSize) q.set('pageSize', String(params.pageSize));
      const qs = q.toString();
      return unwrap(
        await get<PaginatedResponse<WzListItem>>(`${base(nip)}/wz${qs ? `?${qs}` : ''}`),
        'Failed to fetch WZ documents',
      );
    },
    [get],
  );

  const getWz = useCallback(
    async (nip: string, id: string): Promise<WzDetail> =>
      unwrap(await get<WzDetail>(`${base(nip)}/wz/${id}`), 'Failed to fetch WZ document'),
    [get],
  );

  const createWz = useCallback(
    async (nip: string, dto: CreateWzDto): Promise<WzDetail> =>
      unwrap(await post<WzDetail>(`${base(nip)}/wz`, dto), 'Failed to create WZ document'),
    [post],
  );

  const updateWz = useCallback(
    async (nip: string, id: string, dto: UpdateWzDto): Promise<WzDetail> =>
      unwrap(await put<WzDetail>(`${base(nip)}/wz/${id}`, dto), 'Failed to update WZ document'),
    [put],
  );

  const deleteWz = useCallback(
    async (nip: string, id: string): Promise<void> =>
      unwrapVoid(await del<void>(`${base(nip)}/wz/${id}`), 'Failed to delete WZ document'),
    [del],
  );

  const downloadWzPdf = useCallback(
    async (nip: string, id: string): Promise<Blob> => getBlob(`${base(nip)}/wz/${id}/pdf`),
    [getBlob],
  );

  const uploadSigned = useCallback(
    async (nip: string, file: File, wzId?: string): Promise<UploadSignedWzResult> => {
      const formData = new FormData();
      formData.append('file', file);
      if (wzId) formData.append('wzId', wzId);
      return unwrap(
        await postFormData<UploadSignedWzResult>(`${base(nip)}/wz/upload-signed`, formData),
        'Failed to upload signed WZ scan',
      );
    },
    [postFormData],
  );

  return {
    listItems, createItem, updateItem, deleteItem,
    listWz, getWz, createWz, updateWz, deleteWz, downloadWzPdf, uploadSigned,
  };
}

/**
 * CRM — Spec: docs/superpowers/specs/2026-09-09-crm-module-design.md
 * Inbound leads arrive through the public API (pap_… token); everything here
 * is the panel side, JWT-authenticated and gated on the 'crm' module.
 */
export function useCrmApi() {
  const { get, post, put, delete: del } = useApi();
  const base = (nip: string) => `/companies/${nip}/crm`;

  const listAccounts = useCallback(
    async (nip: string, params: CrmAccountListParams): Promise<PaginatedResponse<CrmAccountListItem>> => {
      const q = new URLSearchParams();
      if (params.search) q.set('search', params.search);
      if (params.status) q.set('status', params.status);
      if (params.ownerUserId) q.set('ownerUserId', params.ownerUserId);
      if (params.page) q.set('page', String(params.page));
      if (params.pageSize) q.set('pageSize', String(params.pageSize));
      const qs = q.toString();
      return unwrap(
        await get<PaginatedResponse<CrmAccountListItem>>(`${base(nip)}/accounts${qs ? `?${qs}` : ''}`),
        'Failed to fetch CRM accounts',
      );
    },
    [get],
  );

  const getAccount = useCallback(
    async (nip: string, id: string): Promise<CrmAccountDetail> =>
      unwrap(await get<CrmAccountDetail>(`${base(nip)}/accounts/${id}`), 'Failed to fetch account'),
    [get],
  );

  const createAccount = useCallback(
    async (nip: string, dto: UpsertCrmAccountDto): Promise<CrmAccount> =>
      unwrap(await post<CrmAccount>(`${base(nip)}/accounts`, dto), 'Failed to create account'),
    [post],
  );

  const updateAccount = useCallback(
    async (nip: string, id: string, dto: UpsertCrmAccountDto): Promise<CrmAccount> =>
      unwrap(await put<CrmAccount>(`${base(nip)}/accounts/${id}`, dto), 'Failed to update account'),
    [put],
  );

  const listContractorCandidates = useCallback(
    async (nip: string, accountId: string): Promise<CrmContractorCandidate[]> => {
      const res = await get<{ candidates: CrmContractorCandidate[] }>(
        `${base(nip)}/accounts/${accountId}/contractor-candidates`,
      );
      return unwrap(res, 'Failed to fetch contractor candidates').candidates;
    },
    [get],
  );

  const createContractorFromAccount = useCallback(
    async (nip: string, accountId: string): Promise<CrmCreateContractorResult> =>
      unwrap(
        await post<CrmCreateContractorResult>(`${base(nip)}/accounts/${accountId}/create-contractor`, {}),
        'Failed to create contractor',
      ),
    [post],
  );

  const linkContractor = useCallback(
    async (nip: string, accountId: string, contractorId: string | null): Promise<CrmAccount> =>
      unwrap(
        await post<CrmAccount>(`${base(nip)}/accounts/${accountId}/link-contractor`, {
          contractorId,
        } satisfies LinkCrmContractorDto),
        'Failed to link contractor',
      ),
    [post],
  );

  const listContacts = useCallback(
    async (nip: string, accountId?: string, search?: string): Promise<CrmContact[]> => {
      const q = new URLSearchParams();
      if (accountId) q.set('accountId', accountId);
      if (search) q.set('search', search);
      const qs = q.toString();
      return unwrap(
        await get<CrmContact[]>(`${base(nip)}/contacts${qs ? `?${qs}` : ''}`),
        'Failed to fetch contacts',
      );
    },
    [get],
  );

  const createContact = useCallback(
    async (nip: string, dto: UpsertCrmContactDto): Promise<CrmContact> =>
      unwrap(await post<CrmContact>(`${base(nip)}/contacts`, dto), 'Failed to create contact'),
    [post],
  );

  const updateContact = useCallback(
    async (nip: string, id: string, dto: UpsertCrmContactDto): Promise<CrmContact> =>
      unwrap(
        await put<CrmContact>(`${base(nip)}/contacts/${id}`, dto),
        'Failed to update contact',
      ),
    [put],
  );

  const listStages = useCallback(
    async (nip: string): Promise<CrmStage[]> =>
      unwrap(await get<CrmStage[]>(`${base(nip)}/stages`), 'Failed to fetch pipeline stages'),
    [get],
  );

  const listDeals = useCallback(
    async (
      nip: string,
      params: {
        accountId?: string;
        stageId?: string;
        ownerUserId?: string;
        open?: boolean;
        /** ISO instant — only deals closed at/after it (board's 30-day won/lost rails). */
        closedAfter?: string;
      } = {},
    ): Promise<CrmDealListItem[]> => {
      const q = new URLSearchParams();
      if (params.accountId) q.set('accountId', params.accountId);
      if (params.stageId) q.set('stageId', params.stageId);
      if (params.ownerUserId) q.set('ownerUserId', params.ownerUserId);
      if (params.open) q.set('open', 'true');
      if (params.closedAfter) q.set('closedAfter', params.closedAfter);
      const qs = q.toString();
      return unwrap(
        await get<CrmDealListItem[]>(`${base(nip)}/deals${qs ? `?${qs}` : ''}`),
        'Failed to fetch deals',
      );
    },
    [get],
  );

  const createDeal = useCallback(
    async (nip: string, dto: UpsertCrmDealDto): Promise<CrmDealDetail> =>
      unwrap(await post<CrmDealDetail>(`${base(nip)}/deals`, dto), 'Failed to create deal'),
    [post],
  );

  const getDeal = useCallback(
    async (nip: string, id: string): Promise<CrmDealDetail> =>
      unwrap(await get<CrmDealDetail>(`${base(nip)}/deals/${encodeURIComponent(id)}`), 'Failed to fetch deal'),
    [get],
  );

  // Partial body: the board's inline editors send ONE key at a time so two
  // people editing different fields of the same deal never clobber each other.
  const updateDeal = useCallback(
    async (nip: string, id: string, dto: PatchCrmDealDto): Promise<CrmDealDetail> =>
      unwrap(await put<CrmDealDetail>(`${base(nip)}/deals/${id}`, dto), 'Failed to update deal'),
    [put],
  );

  /** Owner pickers: active holders of a deal-owning role in the company (admin/ksiegowy/user/crm). */
  const listCrmUsers = useCallback(
    async (nip: string): Promise<CrmUserOption[]> =>
      unwrap(await get<CrmUserOption[]>(`${base(nip)}/users`), 'Failed to fetch CRM users'),
    [get],
  );

  const changeDealStage = useCallback(
    async (nip: string, id: string, dto: ChangeCrmDealStageDto): Promise<CrmDealDetail> =>
      unwrap(await post<CrmDealDetail>(`${base(nip)}/deals/${id}/stage`, dto), 'Failed to change stage'),
    [post],
  );

  const listLeads = useCallback(
    async (nip: string, params: CrmLeadListParams): Promise<PaginatedResponse<CrmLeadListItem>> => {
      const q = new URLSearchParams();
      if (params.search) q.set('search', params.search);
      if (params.status) q.set('status', params.status);
      if (params.sourceName) q.set('sourceName', params.sourceName);
      if (params.from) q.set('from', params.from);
      if (params.to) q.set('to', params.to);
      if (params.page) q.set('page', String(params.page));
      if (params.pageSize) q.set('pageSize', String(params.pageSize));
      const qs = q.toString();
      return unwrap(
        await get<PaginatedResponse<CrmLeadListItem>>(`${base(nip)}/leads${qs ? `?${qs}` : ''}`),
        'Failed to fetch leads',
      );
    },
    [get],
  );

  const getLead = useCallback(
    async (nip: string, id: string): Promise<CrmLeadDetail> =>
      unwrap(await get<CrmLeadDetail>(`${base(nip)}/leads/${encodeURIComponent(id)}`), 'Failed to fetch lead'),
    [get],
  );

  const createLead = useCallback(
    async (nip: string, dto: CreateCrmLeadDto): Promise<CrmLeadDetail> =>
      unwrap(await post<CrmLeadDetail>(`${base(nip)}/leads`, dto), 'Failed to create lead'),
    [post],
  );

  const convertLead = useCallback(
    async (nip: string, id: string, dto: ConvertCrmLeadDto): Promise<ConvertCrmLeadResult> =>
      unwrap(await post<ConvertCrmLeadResult>(`${base(nip)}/leads/${id}/convert`, dto), 'Failed to convert lead'),
    [post],
  );

  /** Hide one lead. `reasonCode` 'spam' lands on status `spam`, everything else on `rejected`. */
  const rejectLead = useCallback(
    async (nip: string, id: string, body: RejectCrmLeadDto): Promise<CrmLeadDetail> =>
      unwrap(await post<CrmLeadDetail>(`${base(nip)}/leads/${id}/reject`, body), 'Failed to reject lead'),
    [post],
  );

  /** One reason for every selected lead; converted leads come back in `skipped`. */
  const bulkRejectLeads = useCallback(
    async (nip: string, body: BulkRejectCrmLeadsDto): Promise<BulkCrmLeadsResult> =>
      unwrap(await post<BulkCrmLeadsResult>(`${base(nip)}/leads/bulk-reject`, body), 'Failed to hide leads'),
    [post],
  );

  /** Hard delete — company admin / superadmin only; 409 for converted leads. */
  const deleteLead = useCallback(
    async (nip: string, id: string): Promise<{ deleted: true }> =>
      unwrap(
        await del<{ deleted: true }>(`${base(nip)}/leads/${encodeURIComponent(id)}`),
        'Failed to delete lead',
      ),
    [del],
  );

  const bulkDeleteLeads = useCallback(
    async (nip: string, body: CrmLeadIdsDto): Promise<BulkCrmLeadsResult> =>
      unwrap(await post<BulkCrmLeadsResult>(`${base(nip)}/leads/bulk-delete`, body), 'Failed to delete leads'),
    [post],
  );

  const restoreLead = useCallback(
    async (nip: string, id: string): Promise<CrmLeadDetail> =>
      unwrap(await post<CrmLeadDetail>(`${base(nip)}/leads/${id}/restore`, {}), 'Failed to restore lead'),
    [post],
  );

  /** Un-hide leads (any CRM user); converted/already-active ids come back as `skipped`. */
  const bulkRestoreLeads = useCallback(
    async (nip: string, body: CrmLeadIdsDto): Promise<BulkCrmLeadsResult> =>
      unwrap(await post<BulkCrmLeadsResult>(`${base(nip)}/leads/bulk-restore`, body), 'Failed to restore leads'),
    [post],
  );

  const bulkAssignLeads = useCallback(
    async (
      nip: string,
      body: { ids: string[]; ownerUserId?: string | null; accountId?: string | null },
    ): Promise<{ updated: number }> =>
      unwrap(await post<{ updated: number }>(`${base(nip)}/leads/bulk-assign`, body), 'Failed to assign leads'),
    [post],
  );

  const listActivities = useCallback(
    async (nip: string, params: { accountId?: string; dealId?: string; limit?: number }): Promise<CrmActivity[]> => {
      const q = new URLSearchParams();
      if (params.accountId) q.set('accountId', params.accountId);
      if (params.dealId) q.set('dealId', params.dealId);
      if (params.limit) q.set('limit', String(params.limit));
      const qs = q.toString();
      return unwrap(
        await get<CrmActivity[]>(`${base(nip)}/activities${qs ? `?${qs}` : ''}`),
        'Failed to fetch activities',
      );
    },
    [get],
  );

  const createActivity = useCallback(
    async (nip: string, dto: CreateCrmActivityDto): Promise<void> => {
      unwrap(await post<{ ok: true }>(`${base(nip)}/activities`, dto), 'Failed to add activity');
    },
    [post],
  );

  return {
    listAccounts, getAccount, createAccount, updateAccount,
    listContractorCandidates, createContractorFromAccount, linkContractor,
    listContacts, createContact, updateContact,
    listStages, listDeals, getDeal, createDeal, updateDeal, changeDealStage, listCrmUsers,
    listLeads, getLead, createLead, convertLead, rejectLead, bulkRejectLeads,
    deleteLead, bulkDeleteLeads, restoreLead, bulkRestoreLeads, bulkAssignLeads,
    listActivities, createActivity,
  };
}
